import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef } from "react";
import {
  Card,
  Typography,
  Flex,
  Table,
  InputNumber,
  Input,
  Button,
  Tag,
  Select,
  Divider,
  Radio,
  Space,
  message,
  Modal,
  theme,
  DatePicker,
  Collapse,
  Tooltip,
  Dropdown,
  Checkbox,
  Popover,
  Result,
  Switch,
} from "antd";
import type { MenuProps } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  HolderOutlined,
  TruckOutlined,
  ShopOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  DollarOutlined,
  EditOutlined,
  MoreOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import banktransferIcon from "@/components/icon/payment/banktransfer.png";
import cardIcon from "@/components/icon/payment/card.png";
import cashIcon from "@/components/icon/payment/cash.png";
import echeqIcon from "@/components/icon/payment/echeq.png";
import getnetIcon from "@/components/icon/payment/getnet.png";
import mercadopagoIcon from "@/components/icon/payment/mercadopago.png";
import mostradorChannelIcon from "@/components/icon/payment/mostrador.svg";
import onlineChannelIcon from "@/components/icon/payment/online.svg";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  axiosInstance,
  formatPercent,
  formatPrice,
  marginUnitCopy,
  selectAllOnFocus,
} from "@/utilities";
import { API_URL_QUOTES } from "@/services/XCONS_ENV";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  validateSellerLevel,
  hasSellerLevelErrors,
  type QuoteLevelErrors,
  hasQuoteLevelErrors,
} from "../utils/quoteValidations";
import { getStockLabel } from "../utils/stockLabel";
import { useColumnVisibility } from "../utils/useColumnVisibility";
import { useColumnOrder } from "../utils/useColumnOrder";
import { getShippingTypeIcon } from "@/components/icon/shippingTypeIcon";

const TOGGLABLE_COLUMN_KEYS = [
  "brand",
  "commission",
  "margin",
  "shippingType",
  "sku",
  "discount",
  "price",
  "qty",
  "stock",
] as const;
const TOGGLABLE_COLUMN_LABELS: Record<string, string> = {
  brand: "Marca",
  commission: "Comisión",
  // 028 — la de `margin` es un fallback: la etiqueta real depende de la unidad
  // del operador y se resuelve donde se consume (`marginCopy`), porque este
  // mapa es de módulo y la unidad viene del payload. No mutarlo.
  margin: "Margen",
  shippingType: "Tipo carga",
  sku: "SKU",
  discount: "Desc.",
  price: "Precio unit.",
  qty: "Cant.",
  stock: "Stock",
};

/**
 * Header cell that is BOTH a drag source and drop target for column reordering.
 * Only kicks in when `columnKey` is one of the togglable column keys; for
 * fixed/anonymous columns it renders a plain <th>.
 *
 * Uses the SellerBlock's outer DndContext, discriminated in handleDragEnd by
 * `data.current.type === 'column'`.
 */
const ReorderableHeaderCell: React.FC<any> = ({
  columnKey,
  children,
  style,
  ...rest
}) => {
  const deliveryGroup = React.useContext(DeliveryGroupContext);
  const sortApi = React.useContext(ColumnSortContext);
  const activeSort =
    deliveryGroup != null ? sortApi?.sortState[deliveryGroup] : undefined;
  const sortDir =
    activeSort && activeSort.columnKey === columnKey ? activeSort.dir : null;
  const onSort =
    sortApi && deliveryGroup != null
      ? () => sortApi.onSort(deliveryGroup, columnKey)
      : undefined;

  const dragId = `column-${columnKey}`;
  const drag = useDraggable({ id: dragId, data: { type: "column", columnKey } });
  const drop = useDroppable({ id: dragId, data: { type: "column", columnKey } });

  const setRefs = (node: HTMLElement | null) => {
    drag.setNodeRef(node);
    drop.setNodeRef(node);
  };

  return (
    <th
      ref={setRefs as any}
      {...rest}
      style={{
        ...style,
        opacity: drag.isDragging ? 0.4 : 1,
        background: drop.isOver && !drag.isDragging ? "#fafafa" : undefined,
        transition: "background 0.12s",
      }}
    >
      <Flex align="center" gap={4}>
        <span
          {...drag.attributes}
          {...drag.listeners}
          style={{
            cursor: drag.isDragging ? "grabbing" : "grab",
            display: "inline-flex",
            alignItems: "center",
            color: "#bfbfbf",
            lineHeight: 1,
          }}
          aria-label="Reordenar columna"
        >
          <HolderOutlined />
        </span>
        {/*
          OpenSpec 026 — el click de sort va en el LABEL, no en el `<th>`.
          Los listeners del drag de columnas viven sólo en el grip de arriba, así que no hace falta
          ninguna heurística de umbral para distinguir click de arrastre: son dos zonas separadas.
          El design lo había marcado como el punto delicado de la fase, y resultó que no lo era.
        */}
        <span
          style={{ flex: 1, cursor: onSort ? "pointer" : undefined, userSelect: "none" }}
          onClick={onSort}
          title={onSort ? "Ordenar por esta columna" : undefined}
        >
          {children}
          {sortDir && (
            <span style={{ marginLeft: 4, color: "#1890ff", fontSize: 10 }}>
              {sortDir === "asc" ? "▲" : "▼"}
            </span>
          )}
        </span>
      </Flex>
    </th>
  );
};

/**
 * Header cell wrapper. Routes reorderable columns through ReorderableHeaderCell
 * (which uses dnd-kit hooks) and renders a plain <th> for everything else.
 * Splitting them avoids hook-order issues and duplicate dnd-kit ids.
 */
const DraggableHeaderCell: React.FC<any> = (props) => {
  const { columnKey, children, style, ...rest } = props;
  const isReorderable =
    typeof columnKey === "string" &&
    (TOGGLABLE_COLUMN_KEYS as readonly string[]).includes(columnKey);

  if (isReorderable) {
    return <ReorderableHeaderCell {...props} />;
  }
  // 026 (D4) — las columnas FIJAS (Producto, Total) no se pueden arrastrar ni ocultar, pero **sí se
  // ordenan**. Ordenar por producto es el sort más pedido, y dejarlo afuera sólo porque la columna es
  // fija sería una limitación del andamiaje, no una decisión de producto.
  if (typeof columnKey === "string" && columnKey !== "actions") {
    return <SortableHeaderCell {...props} />;
  }
  return (
    <th {...rest} style={style}>
      {children}
    </th>
  );
};

const ERROR_RED = "#dc3545";
const ERROR_BG = "#fff1f0";

/** Payment method codes that can be placed headless (mirror of API constant). */
const OFFLINE_PAYMENT_METHODS = ["purchaseorder", "checkmo", "banktransfer"];

const { Text, Title } = Typography;

/** Drag handle for each item row */
const DragHandle: React.FC<{ itemId: number; deliveryGroup: number }> = ({
  itemId,
  deliveryGroup,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `item-${itemId}`,
    data: { itemId, deliveryGroup },
  });
  return (
    <span
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ cursor: "grab", opacity: isDragging ? 0.4 : 1 }}
    >
      <HolderOutlined style={{ color: "#ccc" }} />
    </span>
  );
};

/**
 * OpenSpec 026 — la entrega de la fila que se está renderizando.
 *
 * `components.body.row` de antd no recibe la fila como prop, sólo su `data-row-key`; y la fila
 * necesita saber su entrega para poder ser destino de un reorden. Va por contexto en vez de fabricar
 * un componente por entrega: **un tipo de componente nuevo en cada render remontaría todas las
 * filas**, y hay inputs adentro — el operador perdería el foco mientras tipea.
 */
const DeliveryGroupContext = React.createContext<number | null>(null);

/**
 * OpenSpec 026 — el sort, expuesto por contexto.
 *
 * Las columnas (`displayColumns`) se construyen UNA vez y se comparten entre todas las entregas, así
 * que un `onHeaderCell` no puede saber en qué entrega está su tabla. Y el sort es **por entrega**
 * (D2). El contexto lo resuelve sin duplicar la definición de columnas por entrega, que además haría
 * remontar las celdas en cada render.
 */
const ColumnSortContext = React.createContext<{
  onSort: (dg: number, columnKey: string) => void;
  sortState: Record<number, { columnKey: string; dir: "asc" | "desc" }>;
} | null>(null);

/**
 * OpenSpec 026 (D6) — cada fila es zona de drop, para poder reordenar DENTRO de la entrega.
 *
 * Antes sólo la entrega entera era droppable, así que el drop no sabía **sobre qué fila** se soltó y
 * la única semántica posible era "mover a esta entrega". Con la fila como destino, el mismo gesto
 * cubre los dos casos: otra entrega mueve, la misma entrega reordena.
 *
 * Los sub-ítems del optimizador quedan afuera (`disabled`): su lugar lo define el ítem padre.
 */
const DroppableRow: React.FC<any> = ({ children, ...props }) => {
  const deliveryGroup = React.useContext(DeliveryGroupContext);
  const rowKey = String(props["data-row-key"] ?? "");
  const match = rowKey.match(/^item-(\d+)$/);
  const itemId = match ? Number(match[1]) : null;

  const { setNodeRef, isOver } = useDroppable({
    id: `row-${rowKey}`,
    data: itemId != null ? { itemId, deliveryGroup } : {},
    disabled: itemId == null || deliveryGroup == null,
  });

  return (
    <tr
      {...props}
      ref={setNodeRef as any}
      style={{
        ...(props.style || {}),
        // La línea marca DÓNDE va a caer la fila, que es la única pregunta que el operador tiene
        // encima mientras arrastra.
        boxShadow: isOver ? "inset 0 2px 0 0 #1890ff" : undefined,
      }}
    >
      {children}
    </tr>
  );
};

/**
 * OpenSpec 026 (D4) — header **sólo ordenable**, para las columnas fijas.
 *
 * Comparte con `ReorderableHeaderCell` la lectura de los contextos y el indicador, pero sin los hooks
 * de dnd-kit: `Producto` y `Total` no se reordenan ni se ocultan. Se separan en dos componentes por
 * el mismo motivo que ya separaba `DraggableHeaderCell` — condicionar hooks dentro de un componente
 * rompe el orden de hooks de React.
 */
const SortableHeaderCell: React.FC<any> = ({
  columnKey,
  children,
  style,
  ...rest
}) => {
  const deliveryGroup = React.useContext(DeliveryGroupContext);
  const sortApi = React.useContext(ColumnSortContext);
  const activeSort =
    deliveryGroup != null ? sortApi?.sortState[deliveryGroup] : undefined;
  const sortDir =
    activeSort && activeSort.columnKey === columnKey ? activeSort.dir : null;
  const onSort =
    sortApi && deliveryGroup != null
      ? () => sortApi.onSort(deliveryGroup, columnKey)
      : undefined;

  return (
    <th {...rest} style={style}>
      <span
        style={{ cursor: onSort ? "pointer" : undefined, userSelect: "none" }}
        onClick={onSort}
        title={onSort ? "Ordenar por esta columna" : undefined}
      >
        {children}
        {sortDir && (
          <span style={{ marginLeft: 4, color: "#1890ff", fontSize: 10 }}>
            {sortDir === "asc" ? "▲" : "▼"}
          </span>
        )}
      </span>
    </th>
  );
};

/** Droppable zone per delivery group */
const DroppableGroup: React.FC<{
  groupId: number;
  children: React.ReactNode;
}> = ({ groupId, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `group-${groupId}`,
    data: { deliveryGroup: groupId },
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transition: "outline 0.15s, background 0.15s",
        outline: isOver ? "2px dashed #1890ff" : "2px dashed transparent",
        background: isOver ? "rgba(24,144,255,0.04)" : "transparent",
        borderRadius: 4,
      }}
    >
      <DeliveryGroupContext.Provider value={groupId}>
        {children}
      </DeliveryGroupContext.Provider>
    </div>
  );
};

const paymentIconImg = (src: string, alt: string) => (
  <img
    src={src}
    alt={alt}
    style={{ width: 26, height: 26, objectFit: "contain" }}
  />
);

const ShippingDiscountInput: React.FC<{
  initial: number;
  onSave: (val: number) => void;
}> = ({ initial, onSave }) => {
  const commit = (e: any) => {
    const raw = String(e?.target?.value ?? "").replace("%", "").trim();
    const n = parseFloat(raw);
    const val = isNaN(n) ? 0 : n;
    console.log("[ShippingDiscount] commit", { raw, val, initial });
    if (val !== initial) onSave(val);
  };
  return (
    <InputNumber
      size="small"
      min={0}
      max={100}
      defaultValue={initial}
      onBlur={commit}
      onPressEnter={commit}
      {...selectAllOnFocus}
      style={{ width: 80 }}
      suffix="%"
    />
  );
};

const PAYMENT_ICONS: Record<string, React.ReactNode> = {
  banktransfer: paymentIconImg(banktransferIcon, "Transferencia"),
  firstdata: paymentIconImg(cardIcon, "Tarjetas"),
  checkmo: paymentIconImg(echeqIcon, "eCheq"),
  mercadopago_basic: paymentIconImg(mercadopagoIcon, "Mercado Pago"),
  purchaseorder: paymentIconImg(cashIcon, "Efectivo"),
  argenmagento: paymentIconImg(getnetIcon, "Getnet"),
};

const PaymentChannelIcons: React.FC<{ code: string }> = ({ code }) => {
  const isOffline = OFFLINE_PAYMENT_METHODS.includes(code);
  return (
    <Flex vertical gap={4} style={{ flexShrink: 0 }}>
      {!isOffline && (
        <img src={onlineChannelIcon} alt="Pago online" width={20} height={20} />
      )}
      <img src={mostradorChannelIcon} alt="Pago en mostrador" width={20} height={20} />
    </Flex>
  );
};

interface SellerBlockProps {
  quoteId: number;
  seller: any;
  customerName?: string;
  onStateChange: (sellerCode: string, state: string) => void;
  onRefresh: () => void;
  /** Quote-level errors (owner / addresses). Shown by the parent; here we just
   * need to know if they exist to gate the Confirmar pedido button. */
  quoteLevelErrors?: QuoteLevelErrors;
  /** Called when the operator clicks Confirmar and validation fails — lets the
   * parent flip its submitted flag so quote-level errors become visible. */
  onSubmitAttempt?: (sellerCode: string) => void;
  /** Storefront base URL used to deep-link into Magento (e.g. optimizer result). */
  storeLink?: string;
  /** Called when the operator performs an action other than adding products
   * (B3): create delivery, select shipping/payment, apply discount, confirm
   * order, etc. The parent uses it to close the product search panel. */
  onNonAddAction?: () => void;
  /** Operador Viewer (xcons/xcons-docs#488): los inputs de la tabla de productos —cantidad,
   * descuento, precio y comisión— quedan deshabilitados. Confirmar pedido NO depende de esto:
   * un Viewer puede comprar. */
  readOnly?: boolean;
}

export interface SellerBlockRef {
  reload: () => Promise<void>;
}

export const SellerBlock = forwardRef<SellerBlockRef, SellerBlockProps>(({
  quoteId,
  seller,
  customerName,
  onStateChange,
  onRefresh,
  quoteLevelErrors,
  onSubmitAttempt,
  storeLink,
  onNonAddAction,
  readOnly = false,
}, ref) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);

  /**
   * 025 — se incrementa después de CADA escritura de ítem, y va en la `key` de los inputs.
   *
   * Sin esto, la `key` sólo cambia cuando el valor resultante **difiere del anterior**, y el input se
   * queda con el texto que tipeó el operador. El caso que lo destapó: con el ítem ya en 30 % de
   * descuento, tipear un precio bajo el piso clampea otra vez a 30 % — nada cambia, no hay
   * re-montaje, y el input sigue mostrando el número rechazado como si hubiera entrado.
   *
   * Es el complemento necesario de D4: el input tiene que mostrar SIEMPRE lo que devolvió el
   * backend, y "siempre" incluye los casos en que el resultado es idéntico al anterior.
   *
   * ⚠️ **El bump va DESPUÉS de `loadSellerData()`, nunca antes.** Bumpear antes del refresh
   * re-monta el input contra los datos VIEJOS — o sea, contra el valor que el operador acaba de
   * reemplazar — y lo deja ahí hasta que llega el GET. En un ítem sin descuento eso se ve como un
   * parpadeo a `0` de unos cientos de ms, justo el tiempo del request. Bumpeando después, el input
   * pasa del texto tipeado al valor persistido sin ningún estado intermedio.
   *
   * La única excepción es el camino `403` de `handlePriceChange`: ahí no hay refresh porque no se
   * persistió nada, así que los datos en memoria ya son los correctos y el bump es lo que devuelve
   * el input al precio real.
   */
  const [writeSeq, setWriteSeq] = useState(0);

  /**
   * OpenSpec 026 (D3/C1) — el sort activo por entrega. **Estado de sesión, no persistido.**
   *
   * Lo único que se guarda del orden es la posición del ítem; el criterio no. Así que este estado
   * existe para dos cosas nada más: alternar asc/desc en clicks sucesivos, y pintar la flechita. Al
   * recargar arranca vacío y el header no muestra ningún sort — aunque el orden sea el que el sort
   * dejó. Es la consecuencia directa de D3, no algo a "arreglar".
   */
  const [sortState, setSortState] = useState<
    Record<number, { columnKey: string; dir: "asc" | "desc" }>
  >({});
  const bumpWriteSeq = () => setWriteSeq((n) => n + 1);

  /**
   * 025 (D5) — los ítems cuyo último precio tipeado fue RECHAZADO por falta de `marginAccess`.
   *
   * Es estado de UI, no de servidor: el valor nunca se persistió, así que `prices` no lo refleja y
   * `validateSellerLevel` (que es una función pura sobre lo que devuelve el API) no puede saberlo.
   * Por eso se lleva acá y se suma al gate de más abajo.
   */
  const [priceErrorItemIds, setPriceErrorItemIds] = useState<Set<number>>(
    new Set(),
  );
  const [prices, setPrices] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>(null);
  const [paymentMethods, setPaymentMethods] = useState<any>(null);
  const [shippingByGroup, setShippingByGroup] = useState<
    Record<number, any>
  >({});
  const [optimisticShipping, setOptimisticShipping] = useState<Record<number, string>>({});
  const [optimisticPayment, setOptimisticPayment] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { visibleKeys, setVisibleKeys, isVisible } = useColumnVisibility({
    // bumped to v3 because we added the margin column and want margin/commission
    // visible by default for operators that have access (the permission filter
    // hides them otherwise). Stored selections under v2 reset to the new defaults.
    storageKey: "xcons:quotes:productsTable:visibleColumns:v3",
    togglableKeys: [...TOGGLABLE_COLUMN_KEYS],
    defaultVisibleKeys: [
      "sku",
      "discount",
      "price",
      "qty",
      "stock",
      "margin",
      "commission",
    ],
  });
  const { order: columnOrder, reorder: reorderColumn } = useColumnOrder({
    storageKey: "xcons:quotes:productsTable:columnOrder:v3",
    togglableKeys: [...TOGGLABLE_COLUMN_KEYS],
  });
  const [installments, setInstallments] = useState<any[]>([]);
  const [selectedCard, setSelectedCard] = useState<string>("");
  const [emptyGroups, setEmptyGroups] = useState<number[]>([]);
  const [activeDgState, setActiveDgState] = useState<number | null>(null);
  const [draggingItem, setDraggingItem] = useState<any>(null);
  const [newAdjName, setNewAdjName] = useState("");
  const [newAdjValue, setNewAdjValue] = useState<number | null>(null);
  // Kind drives both adjustmentType (discount vs extra_charge) and includesTax.
  // Presented to the user as 3 explicit choices instead of relying on a
  // negative number to signal a discount.
  const [newAdjKind, setNewAdjKind] = useState<
    "discount" | "extra" | "perception"
  >("discount");

  const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 5 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { distance: 5 } });
  const sensors = useSensors(mouseSensor, touchSensor);

  const sellerCode = seller.sellerCode;
  const basePath = `${API_URL_QUOTES}/${quoteId}/sellers/${sellerCode}`;

  // 028 — los textos del margen según la unidad del operador (`mUp%` / `mDn%`,
  // `Markup` / `Markdown`), o `null` si no ve márgenes. El API manda `null` en
  // `marginAccess` cuando no hay acceso, así que la truthiness sigue siendo
  // segura para el gate de la columna (`isColumnPermitted`) — esto es sólo copy.
  const marginCopy = marginUnitCopy(seller?.marginAccess);

  const loadSellerData = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      // #406 — las automatizaciones de descuento del store (por pago, envío o
      // productos) corren antes de leer los totales, como en la tienda: su
      // `QuoteTotal.php` las dispara en cada refresco. La API espera a que el flow
      // escriba sus ajustes, así que el GET de abajo ya los trae. Si falla, se
      // muestran los totales sin el ajuste: nunca bloquea el presupuesto.
      await axiosInstance.post(`${basePath}/recalculate`).catch((err) => {
        console.error("Error recalculating seller automations", err);
      });
      const [pricesRes, totalsRes, paymentRes] = await Promise.all([
        axiosInstance.get(`${basePath}/prices`),
        axiosInstance.get(`${basePath}/totals`),
        axiosInstance.get(`${basePath}/payment/methods`),
      ]);
      setPrices(pricesRes.data?.items || pricesRes.data || []);
      setTotals(totalsRes.data);
      setPaymentMethods(paymentRes.data);
      // 025 — limpiar el estado de error del precio acá y no sólo en
      // `handlePriceChange`.
      //
      // El borde rojo + "Exceed the limit" significan "el último valor que
      // tipeaste fue RECHAZADO y el input volvió al precio real". Un refresh
      // exitoso ya trae la verdad del servidor, así que el aviso cumplió su
      // función y tiene que irse — sin importar por qué palanca se arregló.
      //
      // Antes sólo lo limpiaba el camino del precio, así que un operador que se
      // comía el 403 y después lo arreglaba bajando por `Desc.` quedaba con el
      // borde rojo pegado y, peor, con *Confirmar pedido* deshabilitado
      // (`hasPriceErrors` es global al bloque). La única salida era recargar la
      // página, que nadie iba a adivinar.
      //
      // Es seguro ponerlo acá: el `catch` del 403 **no** llama a
      // `loadSellerData` —hace `bumpWriteSeq()` y sale—, así que el error
      // sobrevive al rechazo y se limpia recién con la próxima escritura
      // exitosa, que es exactamente lo que se quiere. Y no hay polling: las 18
      // llamadas a `loadSellerData` son todas acciones deliberadas del operador.
      //
      // Limpia el set COMPLETO y no sólo el ítem que se tocó, a propósito: el
      // refresh va seguido de un `bumpWriteSeq()` que re-keyea TODOS los inputs
      // al precio real, así que después de esto ningún input conserva un valor
      // rechazado y un borde rojo en otra fila ya sería mentira.
      setPriceErrorItemIds((prev) => (prev.size ? new Set() : prev));
    } catch (err) {
      console.error("Error loading seller data", err);
    } finally {
      setLoading(false);
    }
  }, [basePath]);

  useEffect(() => {
    loadSellerData(true);
  }, [loadSellerData]);

  useImperativeHandle(ref, () => ({
    reload: () => loadSellerData(),
  }), [loadSellerData]);

  // Initialize empty delivery groups + active delivery group from seller additionalData
  useEffect(() => {
    try {
      const raw = seller.additionalData;
      const ad = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
      setEmptyGroups(ad.emptyDeliveryGroups || []);
      setActiveDgState(
        typeof ad.activeDeliveryGroup === 'number' && ad.activeDeliveryGroup > 0
          ? ad.activeDeliveryGroup
          : null,
      );
    } catch {
      setEmptyGroups([]);
      setActiveDgState(null);
    }
  }, [seller.additionalData]);

  const vendorColor = seller.mainColor || token.colorPrimary;
  const vendorName = seller.sellerName || sellerCode;
  const vendorLogo = seller.logoUrl;

  // Build product info map from quote seller items (comes from GET /quotes/:id)
  const quoteItems: any[] = seller.quoteSellerItems || [];
  const productInfoMap: Record<number, { productName: string; productSku: string; productImage: string }> = {};
  quoteItems.forEach((qi: any) => {
    productInfoMap[qi.itemId] = {
      productName: qi.productName || '',
      productSku: qi.productSku || '',
      productImage: qi.productImage || '',
    };
  });

  // Merge prices with product info, fallback to quote items if no prices loaded.
  // When an item has an optimizer, it stays as the parent row (main product) and
  // its subItems are rendered as expandable children — no synthetic wrapper row.
  const priceItems = Array.isArray(prices) ? prices : [];
  const items = priceItems.length > 0
    ? priceItems.map((item: any) => {
        const enriched = {
          ...item,
          productName:
            item.productName || productInfoMap[item.itemId]?.productName || '',
          productSku:
            item.productSku || productInfoMap[item.itemId]?.productSku || '',
          productImage:
            item.productImage || productInfoMap[item.itemId]?.productImage || '',
        };

        return {
          ...enriched,
          children:
            Array.isArray(item.subItems) && item.subItems.length > 0
              ? item.subItems
              : undefined,
        };
      })
    : quoteItems.map((qi: any) => ({
        ...qi,
        qty: qi.productQty,
        price: qi.price,
      }));

  const deliveryGroups: Record<number, any[]> = {};
  items.forEach((item: any) => {
    const dg = item.deliveryGroup || 1;
    if (!deliveryGroups[dg]) deliveryGroups[dg] = [];
    deliveryGroups[dg].push(item);
  });
  // Merge empty groups (from backend additionalData)
  emptyGroups.forEach((eg) => {
    if (!deliveryGroups[eg]) deliveryGroups[eg] = [];
  });
  const groupKeys = Object.keys(deliveryGroups)
    .map(Number)
    .sort((a, b) => a - b);

  // Effective active DG: explicit value from additionalData, or fallback to
  // the last (max) DG so the default matches the prior behavior.
  const effectiveActiveDg =
    activeDgState != null && groupKeys.includes(activeDgState)
      ? activeDgState
      : groupKeys.length > 0
      ? groupKeys[groupKeys.length - 1]
      : 1;

  const handleSetActiveDg = async (dg: number) => {
    if (dg === effectiveActiveDg) return;
    const previous = activeDgState;
    setActiveDgState(dg); // optimistic
    onNonAddAction?.();
    try {
      await axiosInstance.patch(`${basePath}/active-delivery-group`, {
        deliveryGroup: dg,
      });
      // Refresh the parent quote so sellers[].additionalData.activeDeliveryGroup
      // is fresh — quoteDetail.handleAddProduct reads from there to decide
      // where to drop new products.
      onRefresh();
    } catch (err) {
      setActiveDgState(previous);
      console.error("Error setting active delivery group", err);
      message.error("No se pudo cambiar la entrega activa");
    }
  };

  // Load shipping methods for a delivery group
  const loadShippingMethods = useCallback(
    async (dg: number) => {
      try {
        const { data } = await axiosInstance.get(
          `${basePath}/deliveries/${dg}/shipping/methods`
        );
        setShippingByGroup((prev) => ({ ...prev, [dg]: data }));
      } catch {
        // no shipping available
      }
    },
    [basePath]
  );

  // Fingerprint that changes when items are added/removed/moved between groups
  const itemsFingerprint = items.map((i: any) => `${i.itemId}:${i.deliveryGroup || 1}`).sort().join(",");

  useEffect(() => {
    groupKeys.forEach((dg) => loadShippingMethods(dg));
  }, [itemsFingerprint]); // reload when items or delivery groups change

  const handleQtyChange = async (itemId: number, qty: number) => {
    try {
      await axiosInstance.patch(`${basePath}/items/${itemId}/qty`, { qty });
      await loadSellerData();
    } catch {
      message.error("Error al actualizar cantidad");
    }
  };

  /**
   * OpenSpec 025 (D7) — el aviso de que el valor se **clampeó** al máximo de descuento.
   *
   * Es un **aviso, no un error**: el clampeo deja un estado válido y no bloquea *Confirmar pedido*.
   * Y dice el valor **aplicado**, no sólo que se excedió: antes de 025 el mismo mensaje significaba
   * "no se aplicó nada", así que decir sólo "excede el límite" ahora sería ambiguo — el operador
   * podría creer que su número no entró cuando en realidad entró el máximo.
   */
  const notifyClamped = (clamped: any, applied: any) => {
    if (!clamped) return;
    const aplicado =
      applied?.discountPercent != null
        ? `${applied.discountPercent}%`
        : `${clamped.limit}%`;
    message.warning(
      `El descuento máximo es ${clamped.limit}%. Se aplicó ${aplicado} en lugar de ${clamped.requested}%.`,
    );
  };

  const handleDiscountChange = async (
    itemId: number,
    discountPercent: number
  ) => {
    try {
      // 025 (D7): ya no se rechaza del lado del front. El backend clampea al máximo y devuelve
      // `applied` + `clamped`; acá sólo se avisa. Validar antes de llamar dejaba al operador sin
      // poder aplicar el máximo desde el input.
      const { data } = await axiosInstance.patch(
        `${basePath}/items/${itemId}/discount`,
        { discountPercent },
      );
      notifyClamped(data?.clamped, data?.applied);
      await loadSellerData();
      // El bump va DESPUÉS del refresh, no antes: ver el comentario de `writeSeq`.
      bumpWriteSeq();
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message || err?.response?.data?.error;
      message.error(apiMsg || "Error al aplicar descuento");
    }
  };

  // 028 (D4/D6) — `handleMarginChange` y su `PATCH …/items/:id/margin` se
  // borraron: el margen dejó de ser input. El recargo (precio por encima de la
  // lista, 009) NO se perdió — su entry point pasó a ser `Precio unit.`, que el
  // 025 ya construyó con el techo gateado por `marginAccess` (D8).
  //
  // La ruta del API sigue viva una release más y después se borra: es una ruta en
  // producción, y si algo no inventariado la llama, sacar la columna no lo rompe.

  /**
   * OpenSpec 025 (Fase 3) — el precio de venta como input.
   *
   * Dos respuestas posibles del backend, y se tratan distinto a propósito:
   *
   * - **`403`** (D5): el operador sin `marginAccess` quiso inflar por encima de la base. Es un
   *   **error**: borde rojo y *Confirmar pedido* deshabilitado, porque el valor NO se aplicó y no hay
   *   que dejarlo confirmar creyendo que sí. No se clampea: no hay valor legal al que llevarlo.
   * - **`clamped`** (D7): el precio caía bajo el piso de descuento y se aplicó el piso. Es un
   *   **aviso**: el estado quedó válido y no bloquea nada.
   */
  const handlePriceChange = async (itemId: number, price: number) => {
    try {
      const { data } = await axiosInstance.patch(
        `${basePath}/items/${itemId}/price`,
        { price },
      );
      // El intento anterior de este ítem, si había quedado en error, se limpia.
      setPriceErrorItemIds((prev) => {
        if (!prev.has(itemId)) return prev;
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      notifyClamped(data?.clamped, data?.applied);
      await loadSellerData();
      // El bump va DESPUÉS del refresh, no antes: ver el comentario de `writeSeq`.
      bumpWriteSeq();
    } catch (err: any) {
      const body = err?.response?.data;
      if (err?.response?.status === 403) {
        setPriceErrorItemIds((prev) => new Set(prev).add(itemId));
        const limite = body?.limit != null ? formatPrice(body.limit) : "la lista";
        message.error(
          `No podés vender por encima de ${limite} sin acceso a márgenes.`,
        );
        // El valor no entró: el input tiene que volver al precio real, no quedarse con el rechazado.
        bumpWriteSeq();
        return;
      }
      message.error(body?.message || body?.error || "Error al aplicar el precio");
    }
  };

  const handleCommissionChange = async (
    itemId: number,
    commissionPercent: number
  ) => {
    try {
      await axiosInstance.patch(`${basePath}/items/${itemId}/commission`, {
        commissionPercent,
      });
      await loadSellerData();
      // El bump va DESPUÉS del refresh, no antes: ver el comentario de `writeSeq`.
      bumpWriteSeq();
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message || err?.response?.data?.error;
      message.error(apiMsg || "Error al aplicar comisión");
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    try {
      await axiosInstance.delete(`${basePath}/items/${itemId}`);
      await loadSellerData();
    } catch {
      message.error("Error al eliminar item");
    }
  };

  const handleMoveItem = async (itemId: number, targetGroup: number) => {
    try {
      await axiosInstance.patch(
        `${basePath}/items/${itemId}/delivery-group`,
        { deliveryGroup: targetGroup }
      );
      // Refresh both the seller-scoped data (prices/totals) and the parent
      // quote (to pick up the new `additionalData.activeDeliveryGroup` set
      // server-side after the move).
      await loadSellerData();
      onRefresh();
    } catch {
      message.error("Error al mover item");
    }
  };

  /**
   * OpenSpec 026 — persiste el orden de una entrega.
   *
   * Manda la **secuencia completa**, no un delta: un sort produce una permutación entera, y con dos
   * escritores concurrentes (el operador y el cliente) una secuencia completa es un last-write-wins
   * limpio mientras un delta sobre un estado que ya cambió deja el orden inconsistente.
   *
   * Es el único camino de escritura del orden, y lo comparten los dos gestos — el drag y el sort.
   */
  const persistOrder = async (dg: number, itemIds: number[]) => {
    try {
      await axiosInstance.put(
        `${basePath}/deliveries/${dg}/items/order`,
        { itemIds },
      );
      await loadSellerData();
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message || err?.response?.data?.error;
      message.error(apiMsg || "Error al reordenar los productos");
    }
  };

  /** Los itemIds de una entrega, en el orden que están hoy en pantalla. */
  const currentOrderOf = (dg: number): number[] =>
    (deliveryGroups[dg] || [])
      .filter((it: any) => !it.isSubItem && it.itemId != null)
      .map((it: any) => Number(it.itemId));

  /** Drag manual: mueve `itemId` al lugar que ocupa `overItemId`. */
  const handleReorderWithinGroup = (
    dg: number,
    itemId: number,
    overItemId: number,
  ) => {
    const order = currentOrderOf(dg);
    const from = order.indexOf(itemId);
    const to = order.indexOf(overItemId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...order];
    next.splice(from, 1);
    next.splice(to, 0, itemId);
    persistOrder(dg, next);
  };

  /**
   * Sort por columna (D3) — **un gesto que materializa posiciones**, no un estado guardado.
   *
   * No se persiste el criterio ni la dirección: sólo el orden resultante. De ahí que "sort y orden
   * manual son excluyentes" no necesite lógica — el drag pisa el resultado del sort y el sort pisa el
   * del drag, porque los dos escriben la misma columna.
   *
   * La dirección es estado de sesión, y por eso al recargar el header no muestra ningún sort activo
   * (C1): es la consecuencia directa de no guardar el criterio, no un bug.
   */
  const handleSortColumn = (dg: number, columnKey: string) => {
    const rows = (deliveryGroups[dg] || []).filter(
      (it: any) => !it.isSubItem && it.itemId != null,
    );
    if (rows.length < 2) return;

    const prev = sortState[dg];
    const dir: "asc" | "desc" =
      prev?.columnKey === columnKey && prev.dir === "asc" ? "desc" : "asc";

    /**
     * 🔴 **Cada caso replica EXACTAMENTE la expresión que la columna renderiza.**
     *
     * No es celo: la primera versión ordenaba `sku` por `productSku` mientras la columna mostraba
     * `vendorSku || productSku`. El sort funcionaba perfecto y el resultado en pantalla parecía
     * arbitrario — ordenado por un valor que el operador no ve. Es el peor tipo de bug de sort,
     * porque no se ve como un bug: se ve como "el sort está roto".
     *
     * Si alguna de estas columnas cambia lo que muestra, este switch tiene que cambiar con ella.
     */
    const value = (row: any): number | string => {
      switch (columnKey) {
        case "product":
          // render: `record.productName || record.name || 'Item #id'`
          return String(row.productName || row.name || "").toLowerCase();
        case "sku":
          // render: `record.vendorSku || record.productSku || '-'`
          return String(row.vendorSku || row.productSku || "").toLowerCase();
        case "brand":
          // render: `record.manufacturer || record.brand || record.brandName`
          return String(
            row.manufacturer || row.brand || row.brandName || "",
          ).toLowerCase();
        case "shippingType":
          // render: `record.shippingTypeLabel || record.shippingType || record.shipType`
          return String(
            row.shippingTypeLabel || row.shippingType || row.shipType || "",
          ).toLowerCase();
        case "qty":
          return Number(row.qty) || 0;
        case "price":
          // render: `record.priceWithTax || record.price`
          return Number(row.priceWithTax || row.price || 0);
        case "discount":
          return Number(row.manualDiscountPercent) || 0;
        case "margin":
          return Number(row.marginPercent) || 0;
        case "commission":
          return Number(row.commissionPercent) || 0;
        case "subtotal":
          // render: `(record.priceWithTax || record.price) * (record.qty || 1)`
          return (
            Number(row.priceWithTax || row.price || 0) * (Number(row.qty) || 1)
          );
        case "stock":
          // La columna muestra una etiqueta ("Sin stock" / "+ Stock en N días"), pero se ordena por
          // el número: es la magnitud que la etiqueta describe, y ordenar el texto pondría
          // "Sin stock" entre medio de los disponibles.
          return Number(row.stock) || 0;
        default:
          return String(row.productName || "").toLowerCase();
      }
    };

    const sorted = [...rows].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      let cmp: number;
      if (typeof va === "number" && typeof vb === "number") {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), "es", { numeric: true });
      }
      // Empate: el orden actual manda, así el sort es estable y no baraja lo que ya estaba.
      if (cmp === 0) return 0;
      return dir === "asc" ? cmp : -cmp;
    });

    setSortState((prevState) => ({ ...prevState, [dg]: { columnKey, dir } }));
    persistOrder(
      dg,
      sorted.map((it: any) => Number(it.itemId)),
    );
  };

  const handleSelectShipping = async (
    dg: number,
    rateCode: string,
    rateId: number,
    price?: number,
    priceWithTax?: number
  ) => {
    onNonAddAction?.();
    // Optimistic UI: reflect selection instantly; backend + reload happen after.
    setOptimisticShipping((prev) => ({ ...prev, [dg]: rateCode }));
    try {
      await axiosInstance.post(`${basePath}/deliveries/${dg}/shipping/select`, {
        rateCode,
        rateId,
        ...(price !== undefined ? { price } : {}),
        ...(priceWithTax !== undefined ? { priceWithTax } : {}),
      });
      await loadSellerData();
      await loadShippingMethods(dg);
    } catch {
      message.error("Error al seleccionar envío");
    } finally {
      setOptimisticShipping((prev) => {
        const copy = { ...prev };
        delete copy[dg];
        return copy;
      });
    }
  };

  // Only offered when the delivery has a single rate (see the shipping Radio.Group).
  // "" in optimisticShipping means "no rate" until the reload lands; on error the
  // optimistic value is dropped and the backend selection shows again.
  const handleDeselectShipping = async (dg: number) => {
    onNonAddAction?.();
    setOptimisticShipping((prev) => ({ ...prev, [dg]: "" }));
    try {
      await axiosInstance.delete(`${basePath}/deliveries/${dg}/shipping`);
      await loadSellerData();
      await loadShippingMethods(dg);
    } catch {
      message.error("Error al quitar el envío");
    } finally {
      setOptimisticShipping((prev) => {
        const copy = { ...prev };
        delete copy[dg];
        return copy;
      });
    }
  };

  const handleSelectPayment = async (paymentMethod: string) => {
    onNonAddAction?.();
    setOptimisticPayment(paymentMethod);
    setInstallments([]);
    setSelectedCard("");
    try {
      await axiosInstance.post(`${basePath}/payment/select`, { paymentMethod });
      await loadSellerData();
    } catch {
      message.error("Error al seleccionar pago");
    } finally {
      setOptimisticPayment(null);
    }
  };

  const handleLoadInstallments = async (card: string) => {
    onNonAddAction?.();
    setSelectedCard(card);
    try {
      const { data } = await axiosInstance.get(
        `${basePath}/payment/installments?card=${card}`
      );
      setInstallments(data.options || []);
    } catch {
      setInstallments([]);
    }
  };

  const handleSelectInstallment = async (
    installmentId: number,
    card: string
  ) => {
    onNonAddAction?.();
    try {
      await axiosInstance.post(`${basePath}/payment/select-installment`, {
        installmentId,
        card,
      });
      loadSellerData();
    } catch {
      message.error("Error al seleccionar cuota");
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingItem(null);
    const { active, over } = event;
    if (!over || !active) return;

    // Column reorder: discriminated by data.type.
    if (
      active.data.current?.type === "column" &&
      over.data.current?.type === "column"
    ) {
      const activeKey = active.data.current.columnKey as string | undefined;
      const overKey = over.data.current.columnKey as string | undefined;
      if (activeKey && overKey) {
        reorderColumn(activeKey, overKey);
      }
      return;
    }

    const itemId = active.data.current?.itemId;
    const sourceGroup = active.data.current?.deliveryGroup;
    const targetGroup = over.data.current?.deliveryGroup;
    const overItemId = over.data.current?.itemId;

    if (itemId == null || targetGroup == null) return;

    // OpenSpec 026 (D6) — el MISMO handle, y el destino define la semántica.
    //
    // Esta rama `else` estaba vacía: soltar una fila dentro de su propia entrega no hacía nada. No se
    // inventa un gesto nuevo, se completa el que ya existía.
    if (sourceGroup !== targetGroup) {
      handleMoveItem(itemId, targetGroup);
    } else if (overItemId != null && overItemId !== itemId) {
      handleReorderWithinGroup(sourceGroup, itemId, overItemId);
    }
  };

  const handleCreateDeliveryGroup = async () => {
    onNonAddAction?.();
    try {
      const { data } = await axiosInstance.post(`${basePath}/deliveries`);
      if (data?.deliveryGroup) {
        setEmptyGroups((prev) => [...prev, data.deliveryGroup]);
      }
      // Reload quote so sellers[].additionalData.emptyDeliveryGroups picks
      // up the new group. Without this, handleAddProduct in quoteDetail
      // computes maxDg from stale data and assigns new products to the
      // previous delivery instead of the newly created empty one.
      onRefresh();
    } catch {
      message.error("Error al crear grupo de entrega");
    }
  };

  const [deleteSellerOpen, setDeleteSellerOpen] = useState(false);
  const [deletingSeller, setDeletingSeller] = useState(false);

  const [couponInput, setCouponInput] = useState("");
  const [couponStatus, setCouponStatus] = useState<"idle" | "validating" | "rejected">("idle");
  const [couponMessage, setCouponMessage] = useState<string>("");

  const handleApplyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;

    onNonAddAction?.();
    setCouponStatus("validating");
    setCouponMessage("Validando cupón...");

    try {
      await axiosInstance.post(`${basePath}/coupons`, { code });
    } catch (err: any) {
      setCouponStatus("rejected");
      setCouponMessage(err?.response?.data?.message || "Error al validar cupón");
      return;
    }

    const start = Date.now();
    const POLL_INTERVAL = 300;
    const POLL_TIMEOUT = 3000;
    const statusUrl = `${basePath}/coupons/${encodeURIComponent(code)}/status`;

    const poll = async (): Promise<void> => {
      try {
        const { data } = await axiosInstance.get(statusUrl);
        if (data?.status === "applied") {
          setCouponStatus("idle");
          setCouponMessage("");
          setCouponInput("");
          await loadSellerData();
          message.success(`Cupón ${code} aplicado`);
          return;
        }
        if (data?.status === "rejected") {
          setCouponStatus("rejected");
          setCouponMessage("Cupón inválido");
          return;
        }
      } catch {
        // ignore transient poll errors
      }

      if (Date.now() - start >= POLL_TIMEOUT) {
        setCouponStatus("rejected");
        setCouponMessage("Cupón inválido");
        return;
      }

      setTimeout(poll, POLL_INTERVAL);
    };

    setTimeout(poll, POLL_INTERVAL);
  };

  const handleDeleteSeller = async () => {
    onNonAddAction?.();
    setDeletingSeller(true);
    try {
      const { data } = await axiosInstance.delete(basePath);
      message.success(`Seller ${vendorName} eliminado`);
      setDeleteSellerOpen(false);
      if (data?.sellersRemaining === 0) {
        navigate("/quotes");
      } else {
        onRefresh();
      }
    } catch {
      message.error("Error al eliminar el Seller");
    } finally {
      setDeletingSeller(false);
    }
  };

  const [confirmingOrder, setConfirmingOrder] = useState(false);
  // Holds the order returned by the place-order endpoint while the success
  // Result modal is open. null = modal hidden. The underlying quote is
  // reloaded before the modal opens so closing via the X leaves the panel
  // already reflecting Comprado ON.
  const [placedOrder, setPlacedOrder] = useState<{
    id: number;
    incrementId?: string;
  } | null>(null);

  // Active DGs (with items). Used for validating that each has a shipping rate.
  const activeDeliveryGroups = groupKeys.filter(
    (dg) => (deliveryGroups[dg] || []).length > 0,
  );
  // Shipping current selection per DG (shape: { selected, rate })
  const shippingByDgSelection: Record<
    number,
    { selected?: string; rate?: string } | null
  > = {};
  for (const dg of activeDeliveryGroups) {
    const sel =
      shippingByGroup[dg]?.currentSelection?.[sellerCode]?.[String(dg)] || null;
    shippingByDgSelection[dg] = sel
      ? { selected: sel.selected, rate: sel.rate }
      : null;
  }
  const sellerLevelErrors = validateSellerLevel({
    prices,
    shippingByDg: shippingByDgSelection,
    activeDeliveryGroups,
    paymentMethods,
    selectedCard,
  });
  const quoteHasErrors = !!quoteLevelErrors && hasQuoteLevelErrors(quoteLevelErrors);
  const sellerHasErrors = hasSellerLevelErrors(sellerLevelErrors);
  // 025 (D5) — un precio rechazado deshabilita *Confirmar pedido*. El presupuesto persistido sigue
  // siendo válido (el valor no entró), pero el operador está mirando una intención que no se aplicó:
  // dejarlo confirmar sería dejarlo confirmar un precio que cree que puso y no puso.
  const hasPriceErrors = priceErrorItemIds.size > 0;
  const hasAnyErrors = quoteHasErrors || sellerHasErrors || hasPriceErrors;

  const handleConfirmOrder = async () => {
    // Validation gate: if anything is missing, surface errors and abort. Va antes que el chequeo
    // del owner: un presupuesto sin dueño ya trae `quoteLevelErrors.owner`, y así el click
    // scrollea al bloque del dueño como a cualquier otro campo (xcons/xcons-docs#488).
    if (hasAnyErrors) {
      setSubmitted(true);
      onSubmitAttempt?.(sellerCode);
      // Scroll to the first error in DOM order so it is in the viewport.
      // setTimeout (after the React commit) is enough; `getBoundingClientRect`
      // honours document order across both quote-level and seller-level
      // markers because we use `document.querySelector` globally.
      setTimeout(() => {
        const nodes = Array.from(
          document.querySelectorAll<HTMLElement>('[data-error="true"]'),
        );
        if (nodes.length === 0) return;
        const first = nodes.reduce((top, n) =>
          n.getBoundingClientRect().top < top.getBoundingClientRect().top
            ? n
            : top,
        );
        first.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    // Owner asignado pero sin usuario Magento: no tiene marcador en rojo al que scrollear.
    if (!customerName) {
      message.warning(
        "Asigná un owner con usuario Magento antes de confirmar el pedido",
      );
      return;
    }
    onNonAddAction?.();
    setConfirmingOrder(true);
    // confirmingOrder stays true until either:
    //   a) the success modal is closed via the X (modal's onCancel)
    //   b) an error path resets it explicitly
    // This guarantees the button shows the spinner + "Generando pedido..."
    // for the full duration the modal is visible.
    try {
      const { data } = await axiosInstance.post(`${basePath}/place-order`);
      if (!data?.success) {
        message.error(data?.error || "No se pudo crear el pedido");
        setConfirmingOrder(false);
        return;
      }
      const orderId = data.orderId;
      const incrementId = data.incrementId;
      // Refresh the seller panel BEFORE showing the success modal so that
      // closing it with the X leaves Comprado ON already reflected.
      await loadSellerData();
      setPlacedOrder({ id: orderId, incrementId });
    } catch (err: any) {
      const status = err?.response?.status;
      const apiMsg =
        err?.response?.data?.message || err?.response?.data?.error;
      const alreadyPlaced =
        status === 409 || err?.response?.data?.alreadyPlaced;
      if (alreadyPlaced) {
        Modal.info({
          title: "Pedido ya creado",
          content:
            "Este presupuesto ya tiene un pedido generado para este seller. Si necesitás generar otro, duplicá el presupuesto.",
          okText: "OK",
        });
        await loadSellerData();
        setConfirmingOrder(false);
        return;
      }
      message.error(apiMsg || "Error al confirmar el pedido");
      setConfirmingOrder(false);
    }
  };

  const handleShippingDiscount = async (
    dg: number,
    rateId: number,
    discountPercent: number
  ) => {
    onNonAddAction?.();
    console.log("[ShippingDiscount] PATCH", {
      url: `${basePath}/shipping-discount`,
      body: { deliveryGroup: dg, rateId, discountPercent },
    });
    try {
      const res = await axiosInstance.patch(`${basePath}/shipping-discount`, {
        deliveryGroup: dg,
        rateId,
        discountPercent,
      });
      console.log("[ShippingDiscount] PATCH OK", res.data);
      message.success(
        discountPercent > 0
          ? `Descuento de envío ${discountPercent}% aplicado`
          : "Descuento de envío removido",
      );
      await loadSellerData();
      loadShippingMethods(dg);
    } catch (err: any) {
      console.error("[ShippingDiscount] PATCH FAIL", err?.response?.data || err);
      const apiMsg = err?.response?.data?.message || err?.response?.data?.error;
      message.error(apiMsg || "Error al aplicar descuento de envío");
    }
  };

  const handleDeleteDeliveryGroup = async (dg: number) => {
    onNonAddAction?.();
    try {
      await axiosInstance.delete(`${basePath}/deliveries/${dg}`);
      setEmptyGroups((prev) => prev.filter((g) => g !== dg));
      onRefresh();
    } catch {
      message.error("Error al eliminar grupo de entrega");
    }
  };

  const handleAddAdjustment = async () => {
    if (!newAdjValue || newAdjValue === 0) return;
    onNonAddAction?.();
    const isDiscount = newAdjKind === "discount";
    const isPerception = newAdjKind === "perception";
    const absValue = Math.abs(newAdjValue);
    // Percepción quirk: the operator types a NET amount (e.g. "10.000")
    // because that's how percepciones work tax-wise. We store the GROSS
    // value (× 1.21) with includesTax=true so both API totals AND Magento
    // PlaceOrder consume the value as-is — and tag the row via
    // additionalInformation so the UI can divide back for display.
    const storedValue = isPerception ? absValue * 1.21 : absValue;
    // All adjustments are stored as gross (includesTax=true): regular
    // discount/extra_charge values come from the c/IVA input verbatim, and
    // percepciones are pre-multiplied by 1.21 above so the value is gross too.
    const includesTax = true;
    const additionalInformation = isPerception
      ? JSON.stringify({ kind: "perception" })
      : undefined;
    const defaultName =
      newAdjKind === "discount"
        ? "Descuento"
        : isPerception
          ? "Percepción"
          : "Cargo extra";
    try {
      await axiosInstance.patch(`${API_URL_QUOTES}/${quoteId}`, {
        quoteSellers: [{
          sellerCode,
          totalAdjustments: [{
            adjustmentCode: `adj_${Date.now()}`,
            adjustmentType: isDiscount ? "discount" : "extra_charge",
            valueType: "fixed",
            value: storedValue,
            includesTax,
            ...(additionalInformation ? { additionalInformation } : {}),
            name: newAdjName || defaultName,
          }],
        }],
      });
      setNewAdjName("");
      setNewAdjValue(null);
      setNewAdjKind("discount");
      await loadSellerData();
    } catch {
      message.error("Error al agregar ajuste");
    }
  };

  const handleDeleteAdjustment = async (adjustmentId: number) => {
    onNonAddAction?.();
    try {
      await axiosInstance.delete(`${API_URL_QUOTES}/adjustment/${adjustmentId}`);
      await loadSellerData();
    } catch {
      message.error("Error al eliminar ajuste");
    }
  };

  const columns = [
    {
      title: "",
      width: 28,
      fixed: "left" as const,
      render: (_: any, record: any) => {
        if (record.isSubItem) {
          return (
            <Text style={{ color: "#8c8c8c", fontSize: 18, lineHeight: 1 }}>
              ↳
            </Text>
          );
        }
        return (
          <DragHandle
            itemId={record.itemId}
            deliveryGroup={record.deliveryGroup || 1}
          />
        );
      },
    },
    {
      title: "Producto",
      key: "product",
      width: 280,
      fixed: "left" as const,
      onHeaderCell: () => ({ columnKey: "product" } as any),
      render: (_: any, record: any) => {
        const hasOptimizer = !record.isSubItem && record.optimizerId != null;
        const optimizerUrl = hasOptimizer && storeLink
          ? `${storeLink.replace(/\/$/, "")}/marketplace/optimizer/result/optimizer/${record.optimizerId}/vendor/${sellerCode}`
          : null;
        return (
          <Flex align="center" gap={10}>
            {record.productImage ? (
              <img
                src={record.productImage}
                alt=""
                style={{
                  width: 40,
                  height: 40,
                  objectFit: "contain",
                  borderRadius: 4,
                  border: "1px solid #f0f0f0",
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 4,
                  background: "#f5f5f5",
                  border: "1px solid #f0f0f0",
                  flexShrink: 0,
                }}
              />
            )}
            <Flex vertical gap={4}>
              <Text
                strong
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.3,
                }}
                title={record.productName || record.name || `Item #${record.itemId}`}
              >
                {record.productName || record.name || `Item #${record.itemId}`}
              </Text>
              {hasOptimizer && (
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  iconPosition="end"
                  onClick={() => {
                    if (optimizerUrl) window.open(optimizerUrl, "_blank", "noopener,noreferrer");
                  }}
                  disabled={!optimizerUrl}
                  style={{ alignSelf: "flex-start", fontSize: 11, height: 24 }}
                >
                  Optimización #{record.optimizerCode ?? record.optimizerId}
                </Button>
              )}
              {submitted &&
                record.itemId != null &&
                sellerLevelErrors.outOfStockItemIds.includes(record.itemId) && (
                  <div
                    data-error="true"
                    style={{
                      marginTop: 4,
                      padding: "4px 8px",
                      background: ERROR_BG,
                      border: `1px solid ${ERROR_RED}`,
                      borderRadius: 4,
                      maxWidth: 320,
                    }}
                  >
                    <Text style={{ color: ERROR_RED, fontSize: 11 }}>
                      {t("quotes.validation.stock.out")}
                    </Text>
                  </div>
                )}
              {/* Stock insuficiente para la cantidad pedida, sin reposición. Se dice
                  el número para que el operador sepa a cuánto bajar. */}
              {submitted &&
                record.itemId != null &&
                (() => {
                  const falta = sellerLevelErrors.insufficientStockItems.find(
                    (i) => i.itemId === record.itemId,
                  );
                  if (!falta) return null;
                  return (
                    <div
                      data-error="true"
                      style={{
                        marginTop: 4,
                        padding: "4px 8px",
                        background: ERROR_BG,
                        border: `1px solid ${ERROR_RED}`,
                        borderRadius: 4,
                        maxWidth: 320,
                      }}
                    >
                      <Text style={{ color: ERROR_RED, fontSize: 11 }}>
                        {t("quotes.validation.stock.insufficient", {
                          stock: falta.stock,
                          qty: falta.qty,
                        })}
                      </Text>
                    </div>
                  );
                })()}
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: "Marca",
      key: "brand",
      width: 110,
      onHeaderCell: () => ({ columnKey: "brand" } as any),
      render: (_: any, record: any) => {
        const brand =
          record.manufacturer || record.brand || record.brandName || null;
        return (
          <Text style={{ fontSize: 12 }}>{brand || "-"}</Text>
        );
      },
    },
    {
      title: "Comisión",
      key: "commission",
      width: 90,
      align: "right" as const,
      onHeaderCell: () => ({ columnKey: "commission" } as any),
      render: (_: any, record: any) => {
        const pct = record.commissionPercent;
        const canEdit =
          seller?.commissionLevel === "editor" && !record.isSubItem;
        if (!canEdit) {
          return pct == null ? (
            <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
          ) : (
            <Text style={{ fontSize: 12 }}>{pct}%</Text>
          );
        }
        const initial = Number(pct) || 0;
        const maxDiscount = Number(seller?.maxManualDiscountPercent ?? 15);
        const applyChange = (val: number) => {
          if (val === initial) return;
          if (val > maxDiscount) {
            message.error(`Comisión máxima permitida: ${maxDiscount}%`);
            return;
          }
          handleCommissionChange(record.itemId, val);
        };
        return (
          <InputNumber
            size="small"
            min={0}
            max={maxDiscount}
            defaultValue={initial}
            disabled={readOnly}
            key={`comm-${record.itemId}-${initial}-${writeSeq}`}
            step={0.01}
            precision={2}
            onBlur={(e) => applyChange(parseFloat(e.target.value) || 0)}
            onPressEnter={(e: any) =>
              applyChange(parseFloat(e.target.value) || 0)
            }
            {...selectAllOnFocus}
            style={{ width: 70 }}
            suffix="%"
          />
        );
      },
    },
    {
      // 028 (F3/F5) — la etiqueta dice la UNIDAD, y el `title` nativo lleva la
      // fórmula. Va como atributo y no como `Tooltip` de antd porque este `<th>`
      // tiene dos gestos vivos (el grip reordena columnas, el click en el label
      // ordena filas) y un atributo no puede interferir con ninguno.
      //
      // El texto incluye el hint de ordenar: el label que envuelve a esto ya trae
      // `title="Ordenar por esta columna"`, y el browser muestra el del elemento
      // más interno — sin repetirlo, esta sería la única columna que lo pierde.
      title: marginCopy ? (
        <span title={marginCopy.tooltip}>{marginCopy.column}</span>
      ) : (
        "Margen"
      ),
      key: "margin",
      width: 82,
      align: "right" as const,
      onHeaderCell: () => ({ columnKey: "margin" } as any),
      /**
       * 028 (D4/F2) — el margen es SOLO LECTURA. Ya no es un input: es siempre
       * calculado, y las palancas para moverlo son `Desc.` y `Precio unit.`, que
       * ya son inputs y ya comparten el núcleo de persistencia del 025.
       *
       * Se perdió poder pedir "quiero 35% de margen" y dejar que el backend
       * calcule el precio — tradeoff asumido. Lo mitiga que esta celda recalcula
       * en cada edición de las otras dos, así que iterar sobre el precio es
       * inmediato y con feedback en la misma fila.
       *
       * Un mismo render para ítem padre y subítem: antes el subítem tenía la
       * rama de solo lectura y el padre el input, y ahora es la misma cosa.
       * La condición es `pct == null` y **no** `pct > 0` (que es lo que hacía la
       * rama del subítem): un margen negativo es una pérdida y hay que mostrarla.
       * El `null` del API ya cubre "no hay dato" — sin costo cargado, sin acceso,
       * o venta 0 en markup.
       */
      render: (_: any, record: any) => {
        const pct = record.marginPercent;
        return pct == null ? (
          <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
        ) : (
          <Text style={{ fontSize: 12 }}>{pct}%</Text>
        );
      },
    },
    {
      title: "Tipo carga",
      key: "shippingType",
      width: 90,
      align: "center" as const,
      onHeaderCell: () => ({ columnKey: "shippingType" } as any),
      render: (_: any, record: any) => {
        const label =
          record.shippingTypeLabel ||
          record.shippingType ||
          record.shipType ||
          null;
        const iconSrc = getShippingTypeIcon(label);
        if (!iconSrc) {
          return <Text type="secondary" style={{ fontSize: 12 }}>-</Text>;
        }
        return (
          <Tooltip title={label}>
            <img
              src={iconSrc}
              alt={String(label)}
              style={{
                width: 24,
                height: 24,
                objectFit: "contain",
                display: "inline-block",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "SKU",
      key: "sku",
      width: 90,
      onHeaderCell: () => ({ columnKey: "sku" } as any),
      render: (_: any, record: any) => {
        return (
          <Flex vertical>
            <Text style={{ fontSize: 12 }}>{record.vendorSku || record.productSku || "-"}</Text>
          </Flex>
        );
      },
    },
    {
      title: "Desc.",
      key: "discount",
      width: 82,
      onHeaderCell: () => ({ columnKey: "discount" } as any),
      render: (_: any, record: any) => {
        if (record.isSubItem) {
          return record.discountPercent > 0 ? (
            <Text style={{ fontSize: 12 }}>{record.discountPercent}%</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
          );
        }
        const initial = record.manualDiscountPercent || 0;
        // OpenSpec 025 (D2) — los decimales YA NO dependen de `marginAccess`.
        //
        // El 005 los había gateado con el argumento "sin márgenes, impacto cero". Deja de ser cierto
        // con el input de precio (D1): un operador sin `marginAccess` que tipea un precio genera un
        // descuento implícito decimal, así que truncarle el input de descuento lo dejaría sin poder
        // reproducir a mano el valor que el sistema le calculó.
        //
        // Es cambio de comportamiento en vivo para todas las stores SIN `marginAccess`.
        const parseDiscount = (v: string) => parseFloat(v) || 0;
        const applyChange = (val: number) => {
          if (val === initial) return;
          // 025 (D7) — ya no se rechaza acá. El backend clampea al máximo y avisa con el valor
          // aplicado. Cortar antes de llamar impedía justamente aplicar el máximo desde el input.
          handleDiscountChange(record.itemId, val);
        };
        return (
          <InputNumber
            size="small"
            min={0}
            // 025 (D7) — sin `max`: con clampeo, un `max` en el input impide tipear el valor que se
            // querría clampear, y el operador se queda sin ver el aviso que le dice dónde está el
            // límite. El límite lo pone el backend, que es donde tiene que estar (fail-closed).
            defaultValue={initial}
            disabled={readOnly}
            key={`disc-${record.itemId}-${initial}-${writeSeq}`}
            step={0.01}
            precision={2}
            onBlur={(e) => applyChange(parseDiscount(e.target.value))}
            onPressEnter={(e: any) => applyChange(parseDiscount(e.target.value))}
            {...selectAllOnFocus}
            style={{ width: 70 }}
            suffix="%"
          />
        );
      },
    },
    {
      title: "Precio unit.",
      key: "price",
      width: 110,
      align: "right" as const,
      onHeaderCell: () => ({ columnKey: "price" } as any),
      render: (_: any, record: any) => {
        const finalPrice = record.priceWithTax || record.price || 0;
        // Referencia tachada: con acceso a márgenes usamos la lista canónica del
        // catálogo, así se ve tanto con descuento como con recargo (precio
        // inflado por encima de la lista). Sin lista (sub-item o sin
        // marginAccess) → comportamiento previo.
        //
        // 016 — la referencia llega ya en la unidad que se muestra
        // (`listPriceWithTax`: bruta, o neta si la store muestra netos). Componer
        // el IVA acá dejaba el tachado bruto al lado de un precio neto. El
        // fallback cubre un API viejo, donde el flag no existe.
        const listReference =
          record.listPriceWithTax != null
            ? record.listPriceWithTax
            : record.listPrice != null
            ? record.listPrice * (1 + (record.taxPercent || 0) / 100)
            : 0;
        const referencePrice = record.isSubItem
          ? record.oldPriceWithTax || 0
          : listReference > 0
          ? listReference
          : record.oldPrice || 0;
        const priceDiffers = Math.abs(referencePrice - finalPrice) > 0.01;

        // OpenSpec 025 — la referencia tachada se conserva arriba del input: es la lista canónica y
        // sigue siendo la información que le dice al operador cuánto se movió del precio de catálogo.
        const reference = priceDiffers ? (
          <Text
            type="secondary"
            style={{ fontSize: 11, textDecoration: "line-through" }}
          >
            {formatPrice(referencePrice)}
          </Text>
        ) : null;

        // Los sub-ítems y los del optimizador siguen siendo de sólo lectura: su precio es derivado,
        // no lo fija el operador. Mismo criterio que la columna Cant.
        if (record.isSubItem || record.optimizerId != null) {
          return (
            <Flex vertical align="flex-end" style={{ lineHeight: 1.15 }}>
              {reference}
              <Text style={{ fontSize: 13 }}>{formatPrice(finalPrice)}</Text>
            </Flex>
          );
        }

        const hasError = priceErrorItemIds.has(record.itemId);
        // La referencia tachada va FUERA DEL FLUJO, colgada arriba del input.
        //
        // Apilada (que es como estaba) le suma una línea a la celda, así que el input de precio
        // queda ~7px más abajo que los de `Desc.`, `Margen` y `Com.` — que la tabla centra
        // verticalmente en una fila cuya altura la fija la miniatura del producto (40px). El
        // desfasaje aparecía sólo en los ítems con `special_price`, que son justo los que se miran.
        //
        // Absoluta, el input queda centrado como los demás y la referencia usa el espacio libre de
        // la fila más el padding de la celda (8px + 8px contra 13px de texto), sin empujar nada ni
        // pisar la fila de arriba. Se alinea a la derecha, igual que la columna.
        return (
          <Flex vertical align="flex-end" style={{ lineHeight: 1.15 }}>
            <div
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              {reference && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    right: 0,
                    paddingBottom: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {reference}
                </div>
              )}
              <InputNumber
                size="small"
                // 025 (D7/D5) — sin `max`: el techo es una denegación de permiso que resuelve el
                // backend con un 403, y el piso se clampea. Un `max` acá impediría tipear
                // justamente los valores que producen esas dos respuestas, y el operador no vería
                // ninguna.
                min={0}
                step={0.01}
                precision={2}
                status={hasError ? "error" : undefined}
                // Marcador que usa el scroll-al-primer-error de `handleConfirmOrder`.
                data-error={hasError ? "true" : undefined}
                // 025 (D4) — el valor que se muestra es el que devolvió el backend (recalculado
                // desde lo persistido), no el que se tipeó. La `key` lo fuerza a re-montarse cuando
                // cambia, así el input nunca queda mostrando un número que no se guardó.
                defaultValue={finalPrice}
                disabled={readOnly}
                key={`price-${record.itemId}-${finalPrice}-${writeSeq}`}
                onBlur={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  if (Math.abs(val - finalPrice) < 0.01) return;
                  handlePriceChange(record.itemId, val);
                }}
                onPressEnter={(e: any) => {
                  const val = parseFloat(e.target.value) || 0;
                  if (Math.abs(val - finalPrice) < 0.01) return;
                  handlePriceChange(record.itemId, val);
                }}
                {...selectAllOnFocus}
                style={{ width: 100 }}
              />
              {hasError && (
                <Text type="danger" style={{ fontSize: 10 }}>
                  Exceed the limit
                </Text>
              )}
            </div>
          </Flex>
        );
      },
    },
    {
      title: "Cant.",
      key: "qty",
      width: 65,
      onHeaderCell: () => ({ columnKey: "qty" } as any),
      render: (_: any, record: any) => {
        const initial = record.qty || 1;
        if (record.isSubItem || record.optimizerId != null) {
          return <Text style={{ fontSize: 12 }}>{initial}</Text>;
        }
        return (
          <InputNumber
            size="small"
            min={1}
            defaultValue={initial}
            disabled={readOnly}
            key={`qty-${record.itemId}-${initial}`}
            onBlur={(e) => {
              const val = parseInt(e.target.value) || 1;
              if (val !== initial) handleQtyChange(record.itemId, val);
            }}
            onPressEnter={(e: any) => {
              const val = parseInt(e.target.value) || 1;
              if (val !== initial) handleQtyChange(record.itemId, val);
            }}
            {...selectAllOnFocus}
            style={{ width: 52 }}
          />
        );
      },
    },
    {
      title: "Stock",
      key: "stock",
      width: 120,
      onHeaderCell: () => ({ columnKey: "stock" } as any),
      render: (_: any, record: any) => {
        if (record.isSubItem) {
          return <Text type="secondary" style={{ fontSize: 11 }}>-</Text>;
        }
        const label = getStockLabel({
          stock: record.stock,
          leadtime: record.leadtime,
          qty: record.qty,
        });

        if (!label) {
          return <Text type="secondary" style={{ fontSize: 11 }}>-</Text>;
        }

        return (
          <Flex vertical>
            {label.lines.map((line, i) => (
              <Text
                key={line}
                style={{ fontSize: i === 0 ? 11 : 10, color: label.color }}
              >
                {line}
              </Text>
            ))}
          </Flex>
        );
      },
    },
    {
      title: (
        <Flex justify="flex-end" align="center" gap={4}>
          <span>Total</span>
          <Popover
            trigger="click"
            placement="bottomRight"
            destroyTooltipOnHide
            content={
              <Checkbox.Group
                value={visibleKeys}
                onChange={(vals) => setVisibleKeys(vals as string[])}
              >
                <Space direction="vertical">
                  {/*
                    028 (F6) — la entrada del margen dice la unidad. Si quedara en
                    "Margen", el operador prendería algo llamado "Margen" y
                    aparecería una columna llamada `mDn%`.
                    La CLAVE (`k`) no cambia: es lo que persiste la visibilidad y el
                    orden en localStorage, así que cambiar la etiqueta no resetea la
                    configuración de columnas de nadie.
                  */}
                  {TOGGLABLE_COLUMN_KEYS.map((k) => (
                    <Checkbox key={k} value={k}>
                      {k === "margin" && marginCopy
                        ? marginCopy.summary
                        : TOGGLABLE_COLUMN_LABELS[k]}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            }
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Popover>
        </Flex>
      ),
      key: "subtotal",
      width: 150,
      align: "right" as const,
      onHeaderCell: () => ({ columnKey: "subtotal" } as any),
      render: (_: any, record: any) => {
        return (
          <Text style={{ whiteSpace: "nowrap" }}>
            {formatPrice(
              (record.priceWithTax || record.price || 0) * (record.qty || 1),
            )}
          </Text>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 36,
      render: (_: any, record: any) => {
        if (record.isSubItem) return null;
        return (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteItem(record.itemId)}
          />
        );
      },
    },
  ];

  // Apply user-defined column order: keep fixed columns in place, replace
  // togglable slots with the saved order, then drop hidden ones.
  const togglableColMap = new Map<string, any>();
  const colSkeleton: any[] = [];
  columns.forEach((c: any) => {
    if (c.key && (TOGGLABLE_COLUMN_KEYS as readonly string[]).includes(c.key)) {
      togglableColMap.set(c.key, c);
      colSkeleton.push("__tog");
    } else {
      colSkeleton.push(c);
    }
  });
  // Permission gating: margin column requires margin access; commission column
  // requires a level other than no_access. Hides them regardless of the user's
  // visibility toggle. (The backend also omits the data, so this is defense in
  // depth + correct UX.)
  const isColumnPermitted = (key: string) => {
    if (key === "margin") return !!seller?.marginAccess;
    if (key === "commission")
      return (
        !!seller?.commissionLevel && seller.commissionLevel !== "no_access"
      );
    return true;
  };
  let togIdx = 0;
  const displayColumns = colSkeleton
    .map((c) => (c === "__tog" ? togglableColMap.get(columnOrder[togIdx++]) : c))
    .filter((c: any) => c && isVisible(c.key) && isColumnPermitted(c.key));

  const currentPayment = paymentMethods?.currentSelection?.payment_method;
  const currentCard = selectedCard || paymentMethods?.currentSelection?.card || "";

  return (
    <Card
      style={{
        marginBottom: 16,
        borderRadius: 12,
        overflow: "hidden",
      }}
      styles={{
        body: {
          padding: 0,
          border: `4px solid ${vendorColor}`,
          borderRadius: 12,
        },
      }}
    >
      {/* Seller header */}
      <Flex
        align="center"
        justify="space-between"
        gap={12}
        style={{
          padding: "14px 16px",
          background: vendorColor,
        }}
      >
        <Flex align="center" gap={12}>
          {vendorLogo && (
            <img
              src={vendorLogo}
              alt={vendorName}
              style={{ height: 28, objectFit: "contain" }}
            />
          )}
          <Title
            level={5}
            style={{
              margin: 0,
              color: "#fff",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {vendorName}
          </Title>
        </Flex>
        <Flex align="center" gap={8}>
          <Space.Compact size="small">
            <Button
              icon={<PlusOutlined />}
              onClick={handleCreateDeliveryGroup}
            >
              Entrega
            </Button>
            <Tooltip title="Eliminar todos los productos">
              <Button
                icon={<DeleteOutlined />}
                onClick={() => setDeleteSellerOpen(true)}
              />
            </Tooltip>
          </Space.Compact>
        </Flex>
      </Flex>

      <Modal
        open={placedOrder !== null}
        onCancel={() => {
          // Closing via the X resets the Confirmar button so the operator
          // can place another order on the same quote (a quote may produce
          // multiple orders). Back to Quotes / Go to order navigate away,
          // so they don't need to reset the state.
          setPlacedOrder(null);
          setConfirmingOrder(false);
        }}
        footer={null}
        width={520}
        centered
        destroyOnClose
      >
        <Result
          status="success"
          title={`The order #${placedOrder?.incrementId || placedOrder?.id || ""} was successfully generated!`}
          subTitle="You can view and manage it from Orders."
          extra={[
            <Button
              key="back"
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                setPlacedOrder(null);
                navigate("/quotes");
              }}
            >
              Volver a Presupuestos
            </Button>,
            <Button
              key="go"
              type="primary"
              onClick={() => {
                const id = placedOrder?.id;
                setPlacedOrder(null);
                if (id) navigate(`/orders/${id}`);
              }}
            >
              Go to order
            </Button>,
          ]}
        />
      </Modal>

      <Modal
        open={deleteSellerOpen}
        title={`Eliminar a ${vendorName}`}
        okText="Eliminar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true, loading: deletingSeller }}
        cancelButtonProps={{ disabled: deletingSeller }}
        onOk={handleDeleteSeller}
        onCancel={() => setDeleteSellerOpen(false)}
        closable={!deletingSeller}
        maskClosable={!deletingSeller}
      >
        <p>
          Vas a eliminar a <strong>{vendorName}</strong> de este presupuesto.
          Se borrarán todos sus productos, entregas, descuentos y selecciones
          de pago.
        </p>
        <p>Esta acción no se puede deshacer.</p>
      </Modal>

      {/* Delivery groups with items */}
      <DndContext
        sensors={sensors}
        onDragStart={(event) => {
          const itemId = event.active.data.current?.itemId;
          const allItems = Object.values(deliveryGroups).flat();
          setDraggingItem(allItems.find((i: any) => i.itemId === itemId) || null);
        }}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDraggingItem(null)}
      >
      {/* 026 — el sort viaja por contexto: las columnas se comparten entre entregas y el header
          necesita saber en cuál está. Ver ColumnSortContext. */}
      <ColumnSortContext.Provider
        value={{ onSort: handleSortColumn, sortState }}
      >
      {groupKeys.map((dg) => {
        const dgItems = deliveryGroups[dg] || [];
        const isEmpty = dgItems.length === 0;
        const shippingData = shippingByGroup[dg];
        const currentShipping = shippingData?.currentSelection;
        const selectedRate = currentShipping?.[sellerCode]?.[String(dg)]?.rate;
        const selectedRateId = currentShipping?.[sellerCode]?.[String(dg)]?.selected;

        return (
          <DroppableGroup key={dg} groupId={dg}>
            {/* Delivery group header */}
            <Flex
              align="center"
              justify="space-between"
              style={{
                padding: "10px 16px",
                background: "#4a4a4a",
                borderTop: dg > 1 ? "2px dashed #e8e8e8" : "none",
              }}
            >
              <Flex align="center" gap={8}>
                <TruckOutlined style={{ color: "#fff" }} />
                <Text strong style={{ color: "#fff" }}>
                  Entrega {dg}
                </Text>
                <Text style={{ fontSize: 11, color: "rgba(255,255,255,0.65)" }}>
                  {dgItems.reduce((acc, it) => acc + (Number(it.qty) || 1), 0)} productos
                </Text>
                <Switch
                  size="small"
                  checked={dg === effectiveActiveDg}
                  onChange={(checked) => {
                    if (checked) handleSetActiveDg(dg);
                  }}
                  style={
                    dg === effectiveActiveDg
                      ? { backgroundColor: "rgb(255, 143, 31)" }
                      : undefined
                  }
                />
                <Text
                  style={{
                    fontSize: 11,
                    color:
                      dg === effectiveActiveDg
                        ? "rgb(255, 143, 31)"
                        : "rgba(255,255,255,0.65)",
                    fontStyle: dg === effectiveActiveDg ? "normal" : "italic",
                  }}
                >
                  {dg === effectiveActiveDg
                    ? "Los productos se agregaran en esta entrega"
                    : "Selecciona para que los productos se agreguen en esta entrega"}
                </Text>
              </Flex>
              <Flex align="center" gap={12}>
                {groupKeys.length > 1 && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteDeliveryGroup(dg)}
                  />
                )}
              </Flex>
            </Flex>

            {/* Items table or empty state */}
            {isEmpty ? (
              <Flex
                align="center"
                justify="center"
                style={{
                  padding: "24px 16px",
                  color: "#bfbfbf",
                }}
              >
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Arrastrá productos de otro grupo o agregá productos a esta entrega
                </Text>
              </Flex>
            ) : (
              <div style={{ padding: "0 8px" }}>
                <Table
                  dataSource={dgItems}
                  columns={displayColumns}
                  components={{
                    header: { cell: DraggableHeaderCell },
                    // 026 (D6) — la fila como zona de drop, para reordenar dentro de la entrega.
                    body: { row: DroppableRow },
                  }}
                  rowKey={(r: any) => {
                    if (r.isSubItem) {
                      return `sub-${r.parentItemId}-${r.optimizerItemId ?? r.productId}`;
                    }
                    return `item-${r.itemId}`;
                  }}
                  pagination={false}
                  size="small"
                  loading={loading}
                  scroll={{ x: "max-content" }}
                  expandable={{ defaultExpandAllRows: true, showExpandColumn: false }}
                />
                <Flex
                  justify="flex-end"
                  align="center"
                  gap={8}
                  style={{
                    padding: "10px 12px",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Total
                  </Text>
                  <Text strong style={{ fontSize: 14 }}>
                    {formatPrice(
                      dgItems.reduce(
                        (acc: number, it: any) =>
                          acc +
                          (it.rowTotal != null
                            ? it.rowTotal
                            : (it.priceWithTax || it.price || 0) *
                              (it.qty || 1)),
                        0,
                      ),
                    )}
                  </Text>
                </Flex>
              </div>
            )}

            {/* Shipping for this delivery group */}
            {!isEmpty && shippingData?.methods && shippingData.methods.length > 0 && (
              <div
                style={{
                  padding: "12px 16px",
                  background: "#fafafa",
                  border:
                    submitted &&
                    sellerLevelErrors.dgsWithoutShipping.includes(dg)
                      ? `1px solid ${ERROR_RED}`
                      : undefined,
                  borderRadius:
                    submitted &&
                    sellerLevelErrors.dgsWithoutShipping.includes(dg)
                      ? 4
                      : undefined,
                }}
              >
                <Text
                  strong
                  style={{ fontSize: 13, display: "block", marginBottom: 8 }}
                >
                  Método de envío
                </Text>
                <Radio.Group
                  value={optimisticShipping[dg] ?? selectedRate}
                  onChange={(e) => {
                    const rateCode = e.target.value;
                    let rateId = 0;
                    let price: number | undefined;
                    let priceWithTax: number | undefined;
                    for (const m of shippingData.methods) {
                      const found = m.rates?.find(
                        (r: any) => r.code === rateCode
                      );
                      if (found) {
                        rateId = found.rateId;
                        price = found.price !== undefined ? Number(found.price) : undefined;
                        priceWithTax = found.priceWithTax !== undefined ? Number(found.priceWithTax) : undefined;
                        break;
                      }
                    }
                    handleSelectShipping(dg, rateCode, rateId, price, priceWithTax);
                  }}
                  style={{ width: "100%" }}
                >
                  <Space direction="vertical" style={{ width: "100%" }} size={8}>
                    {shippingData.methods.map((method: any) =>
                      method.rates?.map((rate: any) => {
                        const effectiveSelected = optimisticShipping[dg] ?? selectedRate;
                        const isSelected = effectiveSelected === rate.code;
                        // With a single rate, clicking it again clears the
                        // selection; with two or more you can only switch.
                        const rateCount = shippingData.methods.reduce(
                          (n: number, m: any) => n + (m.rates?.length || 0),
                          0,
                        );
                        return (
                          <label
                            key={rate.code}
                            onClick={(e) => {
                              if (!isSelected || rateCount !== 1) return;
                              e.preventDefault();
                              handleDeselectShipping(dg);
                            }}
                            style={{
                              display: "block",
                              cursor: "pointer",
                              padding: "10px 12px",
                              background: "#fff",
                              borderRadius: 8,
                              border: `1px solid ${isSelected ? vendorColor : "#e8e8e8"}`,
                              boxShadow: isSelected
                                ? `0 0 0 1px ${vendorColor}`
                                : "none",
                              transition: "all .15s ease",
                            }}
                          >
                            <Flex align="center" gap={12}>
                              <Radio value={rate.code} style={{ marginRight: 0 }} />
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 6,
                                  background: "#f5f5f5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {rate.code === "storepickup" ? (
                                  <ShopOutlined
                                    style={{ fontSize: 20, color: "#1a3a6b" }}
                                  />
                                ) : (
                                  <TruckOutlined
                                    style={{ fontSize: 20, color: "#1a3a6b" }}
                                  />
                                )}
                              </div>
                              <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                                <Text strong style={{ fontSize: 13 }}>
                                  {rate.title}
                                </Text>
                                {rate.description && (
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 11 }}
                                  >
                                    {rate.description}
                                  </Text>
                                )}
                              </Flex>
                              <Flex align="center" gap={8}>
                                {rate.discountPercent > 0 && (
                                  <Tag
                                    color="green"
                                    style={{ fontSize: 10, marginRight: 0 }}
                                  >
                                    -{rate.discountPercent}%
                                  </Tag>
                                )}
                                {(() => {
                                  // 016 (fuente 5) — el rate trae `price` (neto) y
                                  // `priceWithTax` (bruto). Cuando la store muestra
                                  // netos se pinta el neto; el valor que se manda al
                                  // seleccionar sigue siendo el bruto (más abajo), que
                                  // es lo que se persiste y se cobra.
                                  const original =
                                    (shippingData?.pricesWithoutTax === true
                                      ? rate.price
                                      : rate.priceWithTax) || 0;
                                  const pct = rate.discountPercent || 0;
                                  const final = pct > 0
                                    ? original * (1 - pct / 100)
                                    : original;
                                  return (
                                    <Flex vertical align="flex-end" style={{ lineHeight: 1.15 }}>
                                      {pct > 0 && original > 0 && (
                                        <Text
                                          type="secondary"
                                          style={{
                                            fontSize: 11,
                                            textDecoration: "line-through",
                                          }}
                                        >
                                          {formatPrice(original)}
                                        </Text>
                                      )}
                                      <Text strong style={{ fontSize: 13 }}>
                                        {final > 0
                                          ? formatPrice(final)
                                          : "Gratis"}
                                      </Text>
                                    </Flex>
                                  );
                                })()}
                              </Flex>
                            </Flex>
                            {/* Inline discount input — only on the selected
                                rate, lives inside the shipping item card. */}
                            {isSelected && (
                              <Flex
                                align="center"
                                gap={8}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  marginTop: 10,
                                  paddingTop: 10,
                                  paddingLeft: 52,
                                  borderTop: "1px dashed #f0f0f0",
                                }}
                              >
                                <Text style={{ fontSize: 12, color: "#666" }}>
                                  Desc. envío:
                                </Text>
                                <ShippingDiscountInput
                                  key={`ship-disc-${dg}-${rate.rateId}-${rate.discountPercent || 0}`}
                                  initial={Number(rate.discountPercent) || 0}
                                  onSave={(val) =>
                                    handleShippingDiscount(
                                      dg,
                                      Number(rate.rateId) || 0,
                                      val,
                                    )
                                  }
                                />
                              </Flex>
                            )}
                          </label>
                        );
                      })
                    )}
                  </Space>
                </Radio.Group>
              </div>
            )}
            {submitted &&
              sellerLevelErrors.dgsWithoutShipping.includes(dg) && (
                <Text
                  data-error="true"
                  style={{
                    display: "block",
                    padding: "6px 16px",
                    color: ERROR_RED,
                    fontSize: 12,
                    background: "#fafafa",
                  }}
                >
                  {t("quotes.validation.shipping.method.required")}
                </Text>
              )}
          </DroppableGroup>
        );
      })}
      </ColumnSortContext.Provider>

      {/* Drag overlay */}
      <DragOverlay>
        {draggingItem ? (
          <Flex
            align="center"
            gap={8}
            style={{
              padding: "6px 12px",
              background: "#fff",
              borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "1px solid #d9d9d9",
              maxWidth: 300,
            }}
          >
            {draggingItem.productImage && (
              <img
                src={draggingItem.productImage}
                alt=""
                style={{ width: 28, height: 28, objectFit: "contain", borderRadius: 4 }}
              />
            )}
            <Text ellipsis style={{ fontSize: 12 }}>
              {draggingItem.productName || `Item #${draggingItem.itemId}`}
            </Text>
          </Flex>
        ) : null}
      </DragOverlay>
      </DndContext>

      {/* Payment banner */}
      <Flex
        align="center"
        gap={8}
        style={{
          padding: "10px 16px",
          background: "#4a4a4a",
          color: "#fff",
        }}
      >
        <DollarOutlined style={{ color: "#fff", fontSize: 16 }} />
        <Text strong style={{ color: "#fff", fontSize: 14 }}>
          Pago
        </Text>
      </Flex>

      {/* ===== PAYMENT + TOTALS ===== */}
      <Flex style={{ padding: "16px" }} gap={24} wrap="wrap" align="stretch">
        {/* Payment methods */}
        <div style={{ flex: "6 1 280px", minWidth: 280 }}>
          {/* Discount coupon */}
          <div style={{ marginBottom: 12 }}>
            <Flex
              align="center"
              gap={8}
              style={{
                padding: "10px 12px",
                background: "#fff",
                borderRadius: 8,
                border: `1px solid ${couponStatus === "rejected" ? ERROR_RED : "#e8e8e8"}`,
              }}
            >
              <span style={{ fontSize: 16 }}>🎁</span>
              <Text strong style={{ fontSize: 13, flexShrink: 0 }}>
                Ingresá tu cupón de descuento
              </Text>
              <Input
                size="small"
                placeholder="Ingresá tu código o cupón"
                style={{ flex: 1, minWidth: 120 }}
                value={couponInput}
                onChange={(e) => {
                  setCouponInput(e.target.value);
                  if (couponStatus === "rejected") {
                    setCouponStatus("idle");
                    setCouponMessage("");
                  }
                }}
                onPressEnter={handleApplyCoupon}
                disabled={couponStatus === "validating"}
              />
              <Button
                size="small"
                onClick={handleApplyCoupon}
                loading={couponStatus === "validating"}
                disabled={!couponInput.trim() || couponStatus === "validating"}
              >
                Aplicar
              </Button>
            </Flex>
            {couponMessage && (
              <Text
                style={{
                  display: "block",
                  marginTop: 4,
                  marginLeft: 12,
                  fontSize: 12,
                  color: couponStatus === "rejected" ? ERROR_RED : "#8c8c8c",
                }}
              >
                {couponMessage}
              </Text>
            )}
          </div>

          {/* Payment methods wrapper — red border when submitted+invalid */}
          <div
            style={{
              border:
                submitted &&
                (sellerLevelErrors.paymentMissing ||
                  sellerLevelErrors.paymentIncomplete)
                  ? `1px solid ${ERROR_RED}`
                  : undefined,
              borderRadius:
                submitted &&
                (sellerLevelErrors.paymentMissing ||
                  sellerLevelErrors.paymentIncomplete)
                  ? 8
                  : undefined,
              padding:
                submitted &&
                (sellerLevelErrors.paymentMissing ||
                  sellerLevelErrors.paymentIncomplete)
                  ? 8
                  : 0,
            }}
          >
          {(!paymentMethods?.methods?.length) && !loading && (
            <Flex
              vertical
              align="center"
              style={{ padding: "20px 12px", border: "1px dashed #e8e8e8", borderRadius: 8 }}
            >
              <Text type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
                Este seller aún no tiene métodos de pago configurados.
              </Text>
              <Text type="secondary" style={{ fontSize: 11, textAlign: "center" }}>
                Configurá los métodos en el panel del seller para que aparezcan acá.
              </Text>
            </Flex>
          )}
          <Radio.Group
            value={optimisticPayment ?? currentPayment}
            onChange={(e) => handleSelectPayment(e.target.value)}
            style={{ width: "100%" }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              {paymentMethods?.methods?.map((m: any) => {
                const effectivePayment = optimisticPayment ?? currentPayment;
                const isSelected = effectivePayment === m.code;
                return (
                  <div key={m.code}>
                    <label
                      style={{
                        display: "block",
                        cursor: "pointer",
                        padding: "10px 12px",
                        background: "#fff",
                        borderRadius: 8,
                        border: `1px solid ${isSelected ? "#1a5e2a" : "#e8e8e8"}`,
                        boxShadow: isSelected
                          ? "0 0 0 1px #1a5e2a"
                          : "none",
                        transition: "all .15s ease",
                      }}
                    >
                      <Flex align="center" gap={12}>
                        <Radio value={m.code} style={{ marginRight: 0 }} />
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {PAYMENT_ICONS[m.code] || (
                            <CreditCardOutlined style={{ fontSize: 20, color: "#1a3a6b" }} />
                          )}
                        </div>
                        <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ fontSize: 13 }}>
                            {m.title || m.code}
                          </Text>
                          {m.description && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {m.description}
                            </Text>
                          )}
                        </Flex>
                        <PaymentChannelIcons code={m.code} />
                      </Flex>
                    </label>

                    {/* FirstData card + installments — inline under this method */}
                    {m.code === "firstdata" &&
                      currentPayment === "firstdata" &&
                      m.cardTypes && (
                        <div style={{ marginTop: 8, paddingLeft: 12 }}>
                          <Select
                            size="small"
                            placeholder="Seleccionar tarjeta"
                            value={currentCard || undefined}
                            onChange={(val) => handleLoadInstallments(val)}
                            style={{ width: "100%", marginBottom: 6 }}
                            options={m.cardTypes.map((ct: any) => ({
                              value: ct.code,
                              label: ct.label,
                            }))}
                          />
                          {installments.length > 0 && (
                            <Select
                              size="small"
                              placeholder="Seleccionar cuotas"
                              style={{ width: "100%" }}
                              onChange={(val) =>
                                handleSelectInstallment(val, currentCard || "")
                              }
                              options={installments.map((inst: any) => ({
                                value: inst.installmentId,
                                label: `${inst.installmentNumber} cuotas - ${formatPrice(inst.installmentAmount)} ${inst.interestPercent > 0 ? `(${inst.interestPercent}% interés)` : "sin interés"}`,
                              }))}
                            />
                          )}
                        </div>
                      )}
                  </div>
                );
              })}
            </Space>
          </Radio.Group>
          {submitted &&
            (sellerLevelErrors.paymentMissing ||
              sellerLevelErrors.paymentIncomplete) && (
              <Text
                data-error="true"
                style={{
                  display: "block",
                  color: ERROR_RED,
                  fontSize: 12,
                  marginTop: 6,
                }}
              >
                {t("quotes.validation.payment.method.required")}
              </Text>
            )}
          </div>
        </div>

        {/* Totals — 40% */}
        <div style={{ flex: "2 1 280px", minWidth: 280, display: "flex" }}>
          {totals && (
            <Flex vertical gap={12} style={{ width: "100%", justifyContent: "flex-end" }}>
              {/* Totals details — bordered container */}
              <div
                style={{
                  border: "1px solid #e8e8e8",
                  borderRadius: 8,
                  padding: 12,
                  overflow: "hidden",
                }}
              >
                {/* Subtotal sin descuentos (incluye envío a precio de lista) */}
                {(() => {
                  const hasDiscount =
                    ((totals.itemDiscount || 0) + (totals.shippingDiscount || 0)) > 0;
                  const shippingGross =
                    (totals.shippingTotal || 0) + (totals.shippingDiscount || 0);
                  const subtotalWithShipping =
                    (totals.subtotalWithoutDiscount || 0) + shippingGross;
                  return (
                    <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
                      <Text style={{ fontSize: 13 }}>Subtotal sin descuentos</Text>
                      <Text
                        strong
                        style={{
                          fontSize: 13,
                          textDecoration: hasDiscount ? "line-through" : "none",
                          color: hasDiscount ? "#999" : undefined,
                        }}
                      >
                        {formatPrice(subtotalWithShipping)}
                      </Text>
                    </Flex>
                  );
                })()}

                {/* Ahorraste con descuentos — full-width banner (bleeds through the card padding) */}
                {((totals.itemDiscount || 0) + (totals.shippingDiscount || 0)) > 0 && (
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{
                      background: "#DAE8E0",
                      padding: "8px 12px",
                      margin: "0 -12px 10px",
                    }}
                  >
                    <Flex align="center" gap={6}>
                      <Text style={{ fontSize: 13 }}>Ahorraste con descuentos</Text>
                      <Tooltip
                        title={
                          <Flex vertical gap={4}>
                            {(totals.itemDiscount || 0) > 0 && (
                              <Flex justify="space-between" gap={16}>
                                <span>Descuento en productos</span>
                                <span>-{formatPrice(totals.itemDiscount)}</span>
                              </Flex>
                            )}
                            {(totals.shippingDiscount || 0) > 0 && (
                              <Flex justify="space-between" gap={16}>
                                <span>Descuento en envíos</span>
                                <span>-{formatPrice(totals.shippingDiscount)}</span>
                              </Flex>
                            )}
                          </Flex>
                        }
                      >
                        <InfoCircleOutlined style={{ fontSize: 12, cursor: "help" }} />
                      </Tooltip>
                    </Flex>
                    <Text strong style={{ fontSize: 13 }}>
                      -{formatPrice((totals.itemDiscount || 0) + (totals.shippingDiscount || 0))}
                    </Text>
                  </Flex>
                )}

                {/* Margen — visible only when the operator has margin access.
                    Mismo banner full-width que "Ahorraste con descuentos". */}
                {totals.totalMargin && (
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{
                      background: "#DAE8E0",
                      padding: "8px 12px",
                      margin: "0 -12px 10px",
                    }}
                  >
                    {/* 028 (F4) — la etiqueta dice la unidad: Markup o Markdown. */}
                    <Text style={{ fontSize: 13 }}>
                      {marginCopy?.summary ?? "Margen"}
                    </Text>
                    <Flex align="baseline" gap={6}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {formatPercent(totals.totalMargin.percent)}%
                      </Text>
                      <Text strong style={{ fontSize: 13 }}>
                        {formatPrice(totals.totalMargin.amount)}
                      </Text>
                    </Flex>
                  </Flex>
                )}

                {/* Comisión — visible only when the operator has commission access.
                    Mismo banner full-width que "Ahorraste con descuentos". */}
                {totals.totalCommission && (
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{
                      background: "#DAE8E0",
                      padding: "8px 12px",
                      margin: "0 -12px 10px",
                    }}
                  >
                    <Text style={{ fontSize: 13 }}>Comisión</Text>
                    <Flex align="baseline" gap={6}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {formatPercent(totals.totalCommission.percent)}%
                      </Text>
                      <Text strong style={{ fontSize: 13 }}>
                        {formatPrice(totals.totalCommission.amount)}
                      </Text>
                    </Flex>
                  </Flex>
                )}

                {/* Adjustments rows (inline, no nested border) */}
                {totals.totalAdjustments?.map((adj: any) => {
                  // Percepción quirk: stored value is GROSS (× 1.21) with the
                  // `kind: 'perception'` marker in additionalInformation. The
                  // operator entered a NET amount and expects to see it back
                  // — divide by 1.21 and force the "s/IVA" label here.
                  let isPerception = false;
                  try {
                    const meta = adj?.additionalInformation
                      ? typeof adj.additionalInformation === "string"
                        ? JSON.parse(adj.additionalInformation)
                        : adj.additionalInformation
                      : null;
                    isPerception = meta?.kind === "perception";
                  } catch {
                    isPerception = false;
                  }
                  const rawAmount =
                    (adj.valueType === "fixed" ? adj.value : adj.calculatedAmount) || 0;
                  const displayAmount = isPerception ? rawAmount / 1.21 : rawAmount;
                  const taxLabel = isPerception
                    ? "s/IVA"
                    : adj.includesTax === false
                      ? "s/IVA"
                      : "c/IVA";
                  return (
                    <Flex
                      key={adj.adjustmentId || adj.adjustmentCode}
                      align="center"
                      gap={8}
                      style={{ marginBottom: 8 }}
                    >
                      <Input
                        value={adj.name}
                        readOnly
                        style={{ flex: 1 }}
                      />
                      <Input
                        value={`${adj.adjustmentType === "discount" ? "-" : ""}${new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(displayAmount)}`}
                        readOnly
                        prefix={
                          <Space size={4}>
                            <span style={{ color: "#8c8c8c", fontSize: 12 }}>
                              {taxLabel}
                            </span>
                            <span>$</span>
                          </Space>
                        }
                        style={{ width: 170, textAlign: "right" }}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteAdjustment(adj.adjustmentId)}
                      />
                    </Flex>
                  );
                })}

                {/* New adjustment row */}
                <Flex align="center" gap={8}>
                  <Input
                    placeholder="Nombre del ajuste"
                    value={newAdjName}
                    onChange={(e) => setNewAdjName(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <Select
                    size="middle"
                    value={newAdjKind}
                    onChange={(val) => setNewAdjKind(val)}
                    style={{ width: 150 }}
                    options={[
                      {
                        value: "discount",
                        label: (
                          <span>
                            Descuento{" "}
                            <span style={{ color: "#8c8c8c" }}>c/IVA</span>
                          </span>
                        ),
                      },
                      {
                        value: "extra",
                        label: (
                          <span>
                            Recargo{" "}
                            <span style={{ color: "#8c8c8c" }}>c/IVA</span>
                          </span>
                        ),
                      },
                      {
                        value: "perception",
                        label: (
                          <span>
                            Percepción{" "}
                            <span style={{ color: "#8c8c8c" }}>s/IVA</span>
                          </span>
                        ),
                      },
                    ]}
                  />
                  <InputNumber
                    placeholder="00,00"
                    value={newAdjValue}
                    onChange={(val) => setNewAdjValue(val)}
                    onPressEnter={handleAddAdjustment}
                    {...selectAllOnFocus}
                    prefix="$"
                    min={0}
                    style={{ width: 130 }}
                    controls={false}
                  />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddAdjustment}
                    disabled={!newAdjValue || newAdjValue === 0}
                    style={{
                      background:
                        !newAdjValue || newAdjValue === 0 ? undefined : "#1a5e2a",
                      borderColor:
                        !newAdjValue || newAdjValue === 0 ? undefined : "#1a5e2a",
                    }}
                  />
                </Flex>

                {/* Intereses */}
                {(totals.interest || 0) > 0 && (
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{ marginTop: 10 }}
                  >
                    <Text style={{ fontSize: 13 }}>Intereses</Text>
                    <Text style={{ fontSize: 13 }}>
                      {formatPrice(totals.interest)}
                    </Text>
                  </Flex>
                )}
              </div>

              {/* Presupuesto card */}
              <Card
                size="small"
                style={{
                  marginTop: 4,
                  border: "1px solid #d9d9d9",
                  borderRadius: 10,
                }}
                styles={{ body: { padding: "14px 16px" } }}
              >
                <Flex align="center" gap={16}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      border: "2.5px solid #1a5e2a",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <DollarOutlined style={{ fontSize: 28, color: "#1a5e2a" }} />
                  </div>
                  <Flex vertical align="flex-end" style={{ flex: 1 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Presupuesto
                    </Text>
                    <Title level={3} style={{ margin: 0, lineHeight: 1.1 }}>
                      {formatPrice(totals.total || 0)}
                    </Title>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Total sin impuestos {formatPrice(totals.totalWithoutTax || 0)}
                    </Text>
                  </Flex>
                </Flex>
              </Card>

              {/* Confirmar pedido — crea la orden headless (offline) en Magento.
                  Para métodos online (MP/FirstData/Argenmagento) queda disabled por ahora. */}
              {(() => {
                // xcons/xcons-docs#488 — con campos obligatorios incompletos el botón se VE
                // deshabilitado desde el principio, pero sigue recibiendo el click: un `disabled`
                // de antd no dispara `onClick`, y el click es el que marca en rojo y scrollea al
                // primer campo incompleto (`handleConfirmOrder`).
                const incomplete = hasAnyErrors || !customerName;
                const selectedMethodRaw = currentPayment || "";
                const isOnlinePayment =
                  !!selectedMethodRaw &&
                  !OFFLINE_PAYMENT_METHODS.includes(selectedMethodRaw);
                const disabled =
                  confirmingOrder || (!incomplete && isOnlinePayment);
                const looksDisabled = disabled || incomplete;
                const tooltipTitle = incomplete
                  ? "Hay campos obligatorios incompletos"
                  : isOnlinePayment
                    ? "Los métodos de pago online están temporalmente deshabilitados desde el admin"
                    : "";
                return (
                  <Tooltip title={tooltipTitle}>
                    <Button
                      type="primary"
                      block
                      loading={confirmingOrder}
                      disabled={disabled}
                      aria-disabled={looksDisabled}
                      onClick={handleConfirmOrder}
                      style={{
                        background: looksDisabled
                          ? token.colorBgContainerDisabled
                          : "#1a5e2a",
                        borderColor: looksDisabled ? token.colorBorder : "#1a5e2a",
                        color: looksDisabled ? token.colorTextDisabled : "#fff",
                        boxShadow: looksDisabled ? "none" : undefined,
                        borderRadius: 8,
                        height: 42,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: looksDisabled ? "not-allowed" : "pointer",
                      }}
                    >
                      {confirmingOrder
                        ? "Generando pedido..."
                        : `Confirmar pedido${customerName ? ` para ${customerName}` : ""}`}
                    </Button>
                  </Tooltip>
                );
              })()}
            </Flex>
          )}
        </div>
      </Flex>
    </Card>
  );
});
